import { useState, useRef, useMemo, useEffect } from 'react';
import { Link } from 'react-router';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  CheckCircle2,
  RefreshCw,
  Download,
  Upload,
  Search,
  Server,
  FileSpreadsheet,
  Clock,
  ShieldAlert,
  ChevronLeft,
  ChevronRight,
  Coins,
  CheckCheck,
  CreditCard,
  Sparkles,
  X,
  Copy,
  Check,
  Phone,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  TrendingDown,
  TrendingUp,
  History,
  RotateCcw,
  ChevronDown,
  SlidersHorizontal,
  Route,
} from 'lucide-react';
import {
  fetchShippingSummary,
  fetchShippingParcels,
  fetchShippingHistory,
  fetchShippingCarriers,
  fetchPaymentSummary,
  fetchPaymentSettlements,
  triggerShiparonSync,
  testShiparonConnection,
  uploadCarrierCsv,
  uploadSettlementCsv,
  approvePayment,
  syncJetpackTrackApi,
  fetchParcelTimeline,
  fetchLogisticsConfig,
  triggerJetpackPaymentsFetch,
  type ReconciledParcelItem,
} from '../api.js';
import { PageHeader } from '../ui/PageHeader.js';
import { Button } from '../ui/Button.js';
import { Badge, Stat } from '../ui/data.js';
import { Panel } from '../ui/panels.js';
import { TableShell, Table, THead, TBody, Tr, Th, Td } from '../ui/table.js';
import { Loading } from '../ui/feedback.js';
import { formatTnd } from '../lib/format.js';


function formatDateFr(isoDate: string | null | undefined): string {
  if (!isoDate) return '—';
  const clean = isoDate.split('T')[0];
  const parts = clean?.split('-');
  if (parts && parts.length === 3 && parts[0]?.length === 4) {
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }
  return isoDate;
}

function formatDateTimeFr(isoDate: string | Date | null | undefined): string {
  if (!isoDate) return '—';
  try {
    const d = isoDate instanceof Date ? isoDate : new Date(isoDate);
    if (isNaN(d.getTime())) return String(isoDate);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    const hours = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    const sec = String(d.getSeconds()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${min}:${sec}`;
  } catch {
    return String(isoDate);
  }
}

function formatTunisianPhone(raw: string | null | undefined): string {
  if (!raw) return '—';
  const clean = raw.trim();
  const digits = clean.replace(/\D/g, '');
  let d8 = digits;
  if (digits.startsWith('216') && digits.length === 11) {
    d8 = digits.slice(3);
  } else if (digits.length === 8) {
    d8 = digits;
  } else if (digits.length > 8) {
    d8 = digits.slice(-8);
  }
  if (d8.length === 8) {
    return `+216 ${d8.slice(0, 2)} ${d8.slice(2, 5)} ${d8.slice(5, 8)}`;
  }
  return clean;
}

function formatTimeAgo(dateStr: string | null | undefined): string {
  if (!dateStr) return 'Jamais';
  const diffMs = Date.now() - new Date(dateStr).getTime();
  if (diffMs < 0 || isNaN(diffMs)) return 'Récemment';
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'à l\'instant';
  if (diffMin < 60) return `il y a ${diffMin} min`;
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return `il y a ${diffHours} h`;
  const diffDays = Math.floor(diffHours / 24);
  return `il y a ${diffDays} j`;
}

function getStatusBadge(
  status: string,
  mismatchType?: 'exact' | 'underpaid' | 'overpaid' | null,
  priceDiff?: number | null,
) {
  switch (status) {
    case 'ghosted':
      return (
        <span
          title="Colis créé sur Shiparon mais totalement introuvable sur le portail transporteur (aucun scan de prise en charge)"
          className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-rust-100 text-rust-900 border border-rust-500/40 whitespace-nowrap shrink-0"
        >
          <ShieldAlert className="h-3 w-3 text-rust-700 shrink-0" />
          Absent transporteur
        </span>
      );
    case 'price_mismatch':
      if (mismatchType === 'underpaid') {
        return (
          <span
            title="Le transporteur a versé un montant inférieur au COD attendu (Déficit)"
            className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-rust-100 text-rust-900 border border-rust-500/40 whitespace-nowrap shrink-0"
          >
            <TrendingDown className="h-3 w-3 text-rust-700 shrink-0" />
            Sous-payé {priceDiff !== null && priceDiff !== undefined ? `(${formatTnd(Math.abs(priceDiff))})` : ''}
          </span>
        );
      }
      if (mismatchType === 'overpaid') {
        return (
          <span
            title="Le transporteur a versé un montant supérieur au COD attendu (Surplus)"
            className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-herb-100 text-herb-900 border border-herb-500/40 whitespace-nowrap shrink-0"
          >
            <TrendingUp className="h-3 w-3 text-herb-700 shrink-0" />
            Sur-payé {priceDiff !== null && priceDiff !== undefined ? `(+${formatTnd(priceDiff)})` : ''}
          </span>
        );
      }
      return <Badge tone="signal" className="whitespace-nowrap shrink-0">Écart Prix COD</Badge>;
    case 'reconciled_paid':
      return <Badge tone="good" className="whitespace-nowrap shrink-0">Livré & Payé</Badge>;
    case 'reconciled_delivered':
      return <Badge tone="good" className="whitespace-nowrap shrink-0">Livré (Non payé)</Badge>;
    case 'reconciled_in_transit':
      return <Badge tone="scan" className="whitespace-nowrap shrink-0">En cours transporteur</Badge>;
    case 'reconciled_return':
      return <Badge tone="signal" className="whitespace-nowrap shrink-0">Retour</Badge>;
    case 'carrier_only':
      return <Badge tone="muted" className="whitespace-nowrap shrink-0">Transporteur uniquement</Badge>;
    case 'at_depot':
      return (
        <span
          title="Colis stocké au magasin ou dépôt logistique (État 8)"
          className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-sky-100 text-sky-900 border border-sky-400/40 whitespace-nowrap shrink-0"
        >
          Au dépôt (État 8)
        </span>
      );
    case 'unassigned':
      return (
        <span
          title="Commande enregistrée sur Shiparon sans transporteur assigné (champ presta vide)"
          className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-medium bg-cream text-ink-soft border border-border whitespace-nowrap shrink-0"
        >
          Non assigné
        </span>
      );
    case 'unverified':
      return (
        <span
          title="Colis créé sur Shiparon mais non encore interrogé via l'API transporteur en direct"
          className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-amber-100 text-amber-900 border border-amber-500/40 whitespace-nowrap shrink-0"
        >
          <Clock className="h-3 w-3 text-amber-700 shrink-0" />
          En attente API
        </span>
      );
    case 'cancelled':
      return <Badge tone="alarm" className="whitespace-nowrap shrink-0">Annulé (état 6)</Badge>;
    default:
      return <Badge tone="neutral" className="whitespace-nowrap shrink-0">{status}</Badge>;
  }
}

function getPaymentBadge(status: 'unpaid' | 'carrier_paid' | 'approved' | undefined) {
  switch (status) {
    case 'approved':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-herb-100 text-herb-800 border border-herb-600/30 whitespace-nowrap shrink-0">
          <CheckCheck className="h-3 w-3 text-herb-700" />
          Encaissé & Validé MaPara
        </span>
      );
    case 'carrier_paid':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-900 border border-amber-500/30 whitespace-nowrap shrink-0">
          <Clock className="h-3 w-3 text-amber-700" />
          Déclaré Payé (Attente Val.)
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-cream text-ink-soft border border-border whitespace-nowrap shrink-0">
          Non payé
        </span>
      );
  }
}

function SortButton({
  field,
  label,
  currentField,
  direction,
  onSort,
}: {
  readonly field: 'date' | 'price' | 'code';
  readonly label: string;
  readonly currentField: 'date' | 'price' | 'code' | null;
  readonly direction: 'asc' | 'desc';
  readonly onSort: (field: 'date' | 'price' | 'code') => void;
}): React.ReactElement {
  const active = currentField === field;
  return (
    <button
      type="button"
      onClick={() => onSort(field)}
      className={`inline-flex items-center gap-1 font-semibold hover:text-ink transition-colors cursor-pointer select-none ${
        active ? 'text-clay-700' : 'text-ink-soft'
      }`}
    >
      <span>{label}</span>
      {active ? (
        direction === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
      ) : (
        <ArrowUpDown className="h-3 w-3 opacity-40 hover:opacity-100" />
      )}
    </button>
  );
}

function SortTh({
  field,
  label,
  align = 'left',
  currentField,
  direction,
  onSort,
}: {
  readonly field: 'date' | 'price' | 'code';
  readonly label: string;
  readonly align?: 'left' | 'right';
  readonly currentField: 'date' | 'price' | 'code' | null;
  readonly direction: 'asc' | 'desc';
  readonly onSort: (field: 'date' | 'price' | 'code') => void;
}): React.ReactElement {
  return (
    <Th align={align}>
      <SortButton
        field={field}
        label={label}
        currentField={currentField}
        direction={direction}
        onSort={onSort}
      />
    </Th>
  );
}

export function ShiparonPage(): React.ReactElement {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const settlementFileInputRef = useRef<HTMLInputElement>(null);

  const [selectedCarrier, setSelectedCarrier] = useState<string>('all');
  const [activeTab, setActiveTab] = useState<'all' | 'ghosted' | 'unverified' | 'payments' | 'mismatch' | 'unassigned' | 'files'>('all');
  const [mismatchSubtype, setMismatchSubtype] = useState<'all' | 'underpaid' | 'overpaid' | 'exact'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedShiparonEtat, setSelectedShiparonEtat] = useState<string>('all');
  const [datePreset, setDatePreset] = useState<'all' | 'today' | '7d' | '30d' | 'custom'>('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState<'all' | 'unpaid' | 'carrier_paid' | 'approved'>('all');
  const [page, setPage] = useState(1);

  // Portview Optimization States (Req 2)
  const [compactKpis, setCompactKpis] = useState(true);
  const [compactDensity, setCompactDensity] = useState(true);

  // Sorting and selection state
  const [sortField, setSortField] = useState<'date' | 'price' | 'code' | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [selectedParcelDetail, setSelectedParcelDetail] = useState<ReconciledParcelItem | null>(null);
  const [copiedBarcode, setCopiedBarcode] = useState<string | null>(null);

  // Date Range calculation
  const dateParams = useMemo(() => {
    if (datePreset === 'today') {
      const d = new Date().toISOString().split('T')[0];
      return { startDate: `${d}T00:00:00.000Z`, endDate: `${d}T23:59:59.999Z` };
    }
    if (datePreset === '7d') {
      const start = new Date(Date.now() - 7 * 86400000).toISOString().split('T')[0];
      return { startDate: `${start}T00:00:00.000Z`, endDate: undefined };
    }
    if (datePreset === '30d') {
      const start = new Date(Date.now() - 30 * 86400000).toISOString().split('T')[0];
      return { startDate: `${start}T00:00:00.000Z`, endDate: undefined };
    }
    if (datePreset === 'custom') {
      return {
        startDate: customStartDate ? `${customStartDate}T00:00:00.000Z` : undefined,
        endDate: customEndDate ? `${customEndDate}T23:59:59.999Z` : undefined,
      };
    }
    return { startDate: undefined, endDate: undefined };
  }, [datePreset, customStartDate, customEndDate]);

  // Active filters count for quick reset indicator
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (selectedCarrier !== 'all') count++;
    if (selectedStatus !== 'all') count++;
    if (selectedShiparonEtat !== 'all') count++;
    if (datePreset !== 'all') count++;
    if (paymentStatusFilter !== 'all') count++;
    if (searchQuery.trim()) count++;
    return count;
  }, [selectedCarrier, selectedStatus, selectedShiparonEtat, datePreset, paymentStatusFilter, searchQuery]);

  const resetAllFilters = () => {
    setSelectedCarrier('all');
    setSelectedStatus('all');
    setSelectedShiparonEtat('all');
    setDatePreset('all');
    setCustomStartDate('');
    setCustomEndDate('');
    setPaymentStatusFilter('all');
    setSearchQuery('');
    setPage(1);
  };

  // Approval Modal State
  const [approvalModalOpen, setApprovalModalOpen] = useState(false);
  const [targetSettlementId, setTargetSettlementId] = useState<number | null>(null);
  const [targetCodes, setTargetCodes] = useState<string[]>([]);
  const [approvalBankRef, setApprovalBankRef] = useState('');
  const [approvalNotes, setApprovalNotes] = useState('');

  // Sync Date Range State
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const [syncStartDate, setSyncStartDate] = useState('2026-01-01 00:00:00');
  const [syncFullHistory, setSyncFullHistory] = useState(false);

  const [connTestResult, setConnTestResult] = useState<{
    ok: boolean;
    totalOrders?: number;
    latencyMs?: number;
    error?: string;
  } | null>(null);

  const [jetpackPollingActive, setJetpackPollingActive] = useState(false);

  // Queries
  const configQuery = useQuery({
    queryKey: ['shipping', 'config'],
    queryFn: fetchLogisticsConfig,
  });

  const carriersQuery = useQuery({
    queryKey: ['shipping', 'carriers'],
    queryFn: fetchShippingCarriers,
  });

  const hiddenCarriers = useMemo(
    () => (configQuery.data?.config.hiddenCarriers ?? []).map((c) => c.toLowerCase()),
    [configQuery.data],
  );
  const hiddenLivreurs = useMemo(
    () => configQuery.data?.config.hiddenLivreurs ?? [],
    [configQuery.data],
  );

  const visibleCarriers = useMemo(() => {
    const seen = new Set<string>();
    return (carriersQuery.data?.carriers ?? []).filter((c) => {
      const key = (c.carrier || '').trim().toLowerCase();
      if (!key || seen.has(key)) return false;
      if (hiddenCarriers.includes(key)) return false;
      if (c.livreurId !== undefined && hiddenLivreurs.includes(c.livreurId)) return false;
      seen.add(key);
      return true;
    });
  }, [carriersQuery.data, hiddenCarriers, hiddenLivreurs]);

  // If currently selected carrier is hidden, reset to 'all'
  useEffect(() => {
    if (selectedCarrier !== 'all' && hiddenCarriers.includes(selectedCarrier.toLowerCase())) {
      setSelectedCarrier('all');
    }
  }, [selectedCarrier, hiddenCarriers]);

  const summaryQuery = useQuery({
    queryKey: ['shipping', 'summary', selectedCarrier],
    queryFn: () => fetchShippingSummary({ carrier: selectedCarrier }),
    refetchInterval: (query) => {
      const status = query.state.data?.latestSync?.status;
      return status === 'running' ? 2500 : false;
    },
  });

  const paymentsSummaryQuery = useQuery({
    queryKey: ['shipping', 'payments-summary', selectedCarrier],
    queryFn: () => fetchPaymentSummary(selectedCarrier),
    enabled: true,
  });

  const settlementsQuery = useQuery({
    queryKey: ['shipping', 'settlements'],
    queryFn: fetchPaymentSettlements,
  });

  const parcelsQuery = useQuery({
    queryKey: [
      'shipping',
      'parcels',
      selectedCarrier,
      activeTab,
      mismatchSubtype,
      selectedStatus,
      selectedShiparonEtat,
      dateParams,
      paymentStatusFilter,
      searchQuery,
      page,
    ],
    queryFn: () => {
      let statusToFilter: string | undefined = selectedStatus === 'all' ? undefined : selectedStatus;
      // Tab filter only applies when no specific status filter is chosen
      if (selectedStatus === 'all') {
        if (activeTab === 'ghosted') statusToFilter = 'ghosted';
        else if (activeTab === 'unverified') statusToFilter = 'unverified';
        else if (activeTab === 'mismatch') statusToFilter = mismatchSubtype === 'all' ? 'price_mismatch' : mismatchSubtype;
        else if (activeTab === 'unassigned') statusToFilter = 'unassigned';
      }

      return fetchShippingParcels({
        carrier: selectedCarrier === 'all' ? undefined : selectedCarrier,
        status: statusToFilter,
        shiparonEtat: selectedShiparonEtat === 'all' ? undefined : Number(selectedShiparonEtat),
        startDate: dateParams.startDate,
        endDate: dateParams.endDate,
        paymentStatus: activeTab === 'payments' && paymentStatusFilter !== 'all' ? paymentStatusFilter : undefined,
        search: searchQuery.trim() || undefined,
        page,
        limit: 50,
      });
    },
  });

  const historyQuery = useQuery({
    queryKey: ['shipping', 'history'],
    queryFn: () => fetchShippingHistory(),
    refetchInterval: () => {
      return summaryQuery.data?.latestSync?.status === 'running' ? 3000 : false;
    },
  });

  // Watch for sync completion to immediately refresh parcels and reports
  const prevSyncStatusRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const currentStatus = summaryQuery.data?.latestSync?.status;
    if (prevSyncStatusRef.current === 'running' && currentStatus === 'completed') {
      queryClient.invalidateQueries({ queryKey: ['shipping', 'parcels'] });
      queryClient.invalidateQueries({ queryKey: ['shipping', 'carriers'] });
      queryClient.invalidateQueries({ queryKey: ['shipping', 'history'] });
      queryClient.invalidateQueries({ queryKey: ['shipping', 'payments-summary'] });
    }
    prevSyncStatusRef.current = currentStatus;
  }, [summaryQuery.data?.latestSync?.status, queryClient]);

  const timelineQuery = useQuery({
    queryKey: ['shipping', 'parcel-timeline', selectedParcelDetail?.codeBarre],
    queryFn: () => (selectedParcelDetail ? fetchParcelTimeline(selectedParcelDetail.codeBarre) : null),
    enabled: Boolean(selectedParcelDetail?.codeBarre),
  });

  // Mutations
  const testConnMutation = useMutation({
    mutationFn: testShiparonConnection,
    onSuccess: (data) => setConnTestResult(data),
    onError: (err: unknown) =>
      setConnTestResult({ ok: false, error: err instanceof Error ? err.message : 'Erreur de connexion' }),
  });

  const syncMutation = useMutation({
    mutationFn: (params: { startDate?: string; fullHistory?: boolean }) => triggerShiparonSync(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shipping'] });
      setSyncModalOpen(false);
    },
  });

  const uploadDeliveryMutation = useMutation({
    mutationFn: (file: File) => uploadCarrierCsv(file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shipping'] });
      setActiveTab('ghosted');
    },
  });

  const uploadSettlementMutation = useMutation({
    mutationFn: (file: File) => uploadSettlementCsv(file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shipping'] });
      setActiveTab('payments');
    },
  });

  // Auto-refresh while Jetpack verification is processing in background
  useEffect(() => {
    if (!jetpackPollingActive) return;
    const interval = setInterval(() => {
      queryClient.invalidateQueries({ queryKey: ['shipping', 'summary'] });
      queryClient.invalidateQueries({ queryKey: ['shipping', 'parcels'] });
    }, 2500);
    const timeout = setTimeout(() => {
      setJetpackPollingActive(false);
      queryClient.invalidateQueries({ queryKey: ['shipping'] });
    }, 45000);
    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [jetpackPollingActive, queryClient]);

  const syncJetpackTrackMutation = useMutation({
    mutationFn: (params?: { limit?: number; unverifiedOnly?: boolean }) =>
      syncJetpackTrackApi(params?.limit ?? 25000, params?.unverifiedOnly ?? false),
    onSuccess: () => {
      setJetpackPollingActive(true);
      queryClient.invalidateQueries({ queryKey: ['shipping'] });
    },
  });

  // worker job: login to the Jetpack expediteur portal, download the payment
  // file, apply it — ghosts resolved to carrier_paid appear after the run
  const paymentsFetchMutation = useMutation({
    mutationFn: () => triggerJetpackPaymentsFetch(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shipping'] });
    },
  });

  const approveMutation = useMutation({
    mutationFn: approvePayment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shipping'] });
      setApprovalModalOpen(false);
      setApprovalBankRef('');
      setApprovalNotes('');
      setTargetCodes([]);
      setTargetSettlementId(null);
    },
  });

  const summary = summaryQuery.data?.summary;
  const ghostedCount = summary?.ghostedCount ?? 0;
  const ghostedAmount = summary?.ghostedAmount ?? 0;

  const handleSort = (field: 'date' | 'price' | 'code') => {
    if (sortField === field) {
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else {
        setSortField(null);
        setSortDirection('asc');
      }
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // payment/status filters are server-side now (honest pagination)
  const displayedParcels = parcelsQuery.data?.parcels ?? [];
  const sortedParcels = useMemo(() => {
    if (!sortField) return displayedParcels;
    return [...displayedParcels].sort((a, b) => {
      let cmp = 0;
      if (sortField === 'date') {
        const da = a.date ? new Date(a.date).getTime() : 0;
        const db = b.date ? new Date(b.date).getTime() : 0;
        cmp = da - db;
      } else if (sortField === 'price') {
        const pa = a.shiparonPrice ?? 0;
        const pb = b.shiparonPrice ?? 0;
        cmp = pa - pb;
      } else if (sortField === 'code') {
        cmp = a.codeBarre.localeCompare(b.codeBarre);
      }
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  }, [displayedParcels, sortField, sortDirection]);

  const latestSync = summaryQuery.data?.latestSync ?? historyQuery.data?.syncs?.[0];
  const lastSyncAt = latestSync?.createdAt;

  return (
    <div className="space-y-6 animate-fade">
      {/* Page Header */}
      <PageHeader
        index="S16"
        eyebrow="Gestion Logistique · Réconciliation"
        title="Réconciliation Multi-Transporteurs & Double Vérification Financière"
        purpose="Rapprochement 3 niveaux : Shiparon ⇄ API Transporteurs (Jetpack v2 & co) ⇄ Bordereaux de Recette ⇄ Validation Comptable MaPara. Rapprochement depuis le 01/01/2026."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={testConnMutation.isPending}
              onClick={() => testConnMutation.mutate()}
            >
              <Server className="h-4 w-4 mr-1 text-ink-soft" />
              {testConnMutation.isPending ? 'Sondage…' : 'Sonder Shiparon'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={syncMutation.isPending}
              onClick={() => setSyncModalOpen(true)}
            >
              <RefreshCw className={`h-4 w-4 mr-1 text-herb-700 ${syncMutation.isPending ? 'animate-spin' : ''}`} />
              {syncMutation.isPending ? 'Synchronisation…' : 'Sync Shiparon (Live)'}
            </Button>
            {(selectedCarrier === 'all' || selectedCarrier.toLowerCase().includes('jetpack')) && (
              <Button
                variant="outline"
                size="sm"
                disabled={syncJetpackTrackMutation.isPending || jetpackPollingActive}
                onClick={() => syncJetpackTrackMutation.mutate({ limit: 25000, unverifiedOnly: false })}
                title="Vérifier les statuts et paiements en direct via Jetpack API v2"
              >
                <Sparkles className={`h-4 w-4 mr-1 text-scan ${syncJetpackTrackMutation.isPending || jetpackPollingActive ? 'animate-spin' : ''}`} />
                {syncJetpackTrackMutation.isPending || jetpackPollingActive ? 'Vérification API v2…' : 'Suivi API Jetpack v2'}
              </Button>
            )}
            {(selectedCarrier === 'all' || selectedCarrier.toLowerCase().includes('jetpack')) && (
              <Button
                variant="outline"
                size="sm"
                disabled={paymentsFetchMutation.isPending}
                onClick={() => paymentsFetchMutation.mutate()}
                title="Télécharger le fichier de paiement Jetpack depuis leur portail et l'appliquer — résout les colis fantômes payés"
              >
                <Download className={`h-4 w-4 mr-1 text-herb-700 ${paymentsFetchMutation.isPending ? 'animate-spin' : ''}`} />
                {paymentsFetchMutation.isPending ? 'Récupération…' : 'Récupérer paiements Jetpack'}
              </Button>
            )}
          </div>
        }
      />

      
      {/* Carrier Selector Bar */}
      <div className="p-3 bg-card rounded-2xl border border-border shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs font-bold text-ink-soft uppercase tracking-wider px-2">Transporteur :</span>
          <button
            onClick={() => {
              setSelectedCarrier('all');
              setPage(1);
            }}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              selectedCarrier === 'all'
                ? 'bg-clay-600 text-white shadow-xs'
                : 'bg-cream/60 text-ink-soft hover:text-ink hover:bg-cream border border-border/70'
            }`}
          >
            Tous les transporteurs
          </button>
          {visibleCarriers.map((c) => {
            const isSelected = selectedCarrier === (c.rawPresta || 'all');
            return (
              <button
                key={c.carrier}
                onClick={() => {
                  setSelectedCarrier(c.rawPresta || 'all');
                  setPage(1);
                }}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 ${
                  isSelected
                    ? 'bg-clay-600 text-white shadow-xs'
                    : 'bg-cream/60 text-ink-soft hover:text-ink hover:bg-cream border border-border/70'
                }`}
              >
                <span>{c.carrier}</span>
                <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-mono ${
                  isSelected ? 'bg-white/20 text-white' : 'bg-cream text-ink-soft'
                }`}>
                  {c.count.toLocaleString('fr-FR')}
                </span>
              </button>
            );
          })}
        </div>
        <div className="text-xs text-ink-faint px-2">
          Période : <span className="font-semibold text-ink">Depuis le 01/01/2026</span>
        </div>
      </div>

      {/* Freshness & Connection Status Bar (B1, B2) */}
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-2.5 bg-card rounded-xl border border-border text-xs shadow-xs">
        <div className="flex flex-wrap items-center gap-3 sm:gap-4">
          {/* Shiparon Sync Status */}
          <div className="flex items-center gap-1.5 text-ink-soft">
            <span className="relative flex h-2 w-2">
              <span
                className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${
                  latestSync?.status === 'completed'
                    ? 'bg-herb-400'
                    : latestSync?.status === 'running'
                    ? 'bg-amber-400'
                    : 'bg-ink-ghost'
                }`}
              />
              <span
                className={`relative inline-flex rounded-full h-2 w-2 ${
                  latestSync?.status === 'completed'
                    ? 'bg-herb-600'
                    : latestSync?.status === 'running'
                    ? 'bg-amber-500'
                    : 'bg-ink-soft'
                }`}
              />
            </span>
            <span>Dernière synchro Shiparon :</span>
            <strong className="text-ink font-semibold whitespace-nowrap">
              {lastSyncAt ? `${formatDateFr(lastSyncAt)} (${formatTimeAgo(lastSyncAt)})` : 'Jamais'}
            </strong>
            {latestSync?.rowCount !== undefined && latestSync.rowCount > 0 && (
              <span className="text-ink-faint hidden md:inline">
                ({latestSync.rowCount.toLocaleString('fr-FR')} cmd)
              </span>
            )}
          </div>

          <span className="text-border hidden sm:inline">|</span>

          {/* SSH / MySQL Connection Dot (B2) */}
          <div className="flex items-center gap-1.5 text-ink-soft">
            <span
              className={`h-2 w-2 rounded-full shrink-0 ${
                connTestResult?.ok
                  ? 'bg-herb-600'
                  : connTestResult
                  ? 'bg-rust-600'
                  : 'bg-herb-600'
              }`}
            />
            <span>Tunnel SSH / MySQL :</span>
            <span className="font-semibold text-ink whitespace-nowrap">
              {connTestResult?.ok
                ? `OK (${connTestResult.latencyMs}ms)`
                : connTestResult
                ? 'Erreur'
                : 'Connecté (lecture seule)'}
            </span>
          </div>

          <span className="text-border hidden sm:inline">|</span>

          {/* Jetpack Portal API Freshness */}
          <div className="flex items-center gap-1.5 text-ink-soft">
            <Sparkles className="h-3.5 w-3.5 text-scan shrink-0" />
            <span>Portail Jetpack :</span>
            <span className="text-ink whitespace-nowrap">
              {(summary?.unverifiedCount ?? 0) === 0 ? (
                <strong className="text-herb-700 font-semibold">À jour</strong>
              ) : (
                <span className="text-amber-800 font-semibold">
                  {summary?.unverifiedCount?.toLocaleString('fr-FR')} à vérifier
                </span>
              )}
            </span>
          </div>
        </div>

        {/* Quick Refresh Button */}
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            className="text-xs h-7 px-2 text-ink-soft hover:text-ink"
            onClick={() => queryClient.invalidateQueries({ queryKey: ['shipping'] })}
            title="Actualiser les compteurs et listes"
          >
            <RefreshCw className="h-3 w-3 mr-1" />
            Actualiser
          </Button>
        </div>
      </div>

      {/* Live Synchronization Progress Card (active during background sync) */}
      {latestSync?.status === 'running' && (
        <div className="rounded-2xl border-2 border-amber-400 bg-amber-50/90 p-4 sm:p-5 shadow-sm space-y-3 animate-fade">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-start sm:items-center gap-3">
              <div className="relative flex h-5 w-5 shrink-0 mt-0.5 sm:mt-0">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-5 w-5 bg-amber-600 items-center justify-center shadow-xs">
                  <RefreshCw className="h-3 w-3 text-white animate-spin" />
                </span>
              </div>
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h4 className="text-sm font-bold text-amber-950">
                    Synchronisation Shiparon en cours…
                  </h4>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-200 text-amber-900 border border-amber-400/40">
                    <span className="h-1.5 w-1.5 rounded-full bg-amber-600 animate-pulse" />
                    En direct
                  </span>
                </div>
                <p className="text-xs text-amber-900 mt-1 font-medium">
                  {latestSync.progressStage || 'Extraction et réconciliation des données depuis Shiparon MySQL…'}
                </p>
              </div>
            </div>
            <div className="text-left sm:text-right shrink-0 pl-8 sm:pl-0">
              <div className="text-sm font-mono font-bold text-amber-950">
                {latestSync.rowCount.toLocaleString('fr-FR')}
                {latestSync.totalRows && latestSync.totalRows > 0 ? (
                  <span className="text-amber-800 font-normal"> / {latestSync.totalRows.toLocaleString('fr-FR')}</span>
                ) : (
                  ''
                )}{' '}
                colis
              </div>
              {latestSync.totalRows && latestSync.totalRows > 0 ? (
                <div className="text-[11px] font-semibold text-amber-800">
                  {Math.min(99, Math.round((latestSync.rowCount / latestSync.totalRows) * 100))}% complété
                </div>
              ) : (
                <div className="text-[11px] text-amber-700 flex items-center sm:justify-end gap-1">
                  <Clock className="h-3 w-3 animate-spin" /> Ingestion active…
                </div>
              )}
            </div>
          </div>

          {/* Progress bar */}
          <div className="w-full bg-amber-200/80 rounded-full h-2.5 overflow-hidden shadow-inner">
            <div
              className="bg-amber-600 h-2.5 rounded-full transition-all duration-500 ease-out relative"
              style={{
                width:
                  latestSync.totalRows && latestSync.totalRows > 0
                    ? `${Math.max(4, Math.min(100, (latestSync.rowCount / latestSync.totalRows) * 100))}%`
                    : `${Math.min(92, Math.max(12, Math.log10(latestSync.rowCount + 1) * 18))}%`,
              }}
            >
              <div className="absolute inset-0 bg-white/30 animate-pulse" />
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-amber-800">
            <span>
              Démarré {latestSync.createdAt ? formatTimeAgo(latestSync.createdAt) : 'à l’instant'} · Déclenché par{' '}
              <strong className="text-amber-950">{latestSync.triggeredBy || 'système'}</strong>
            </span>
            <span className="italic">
              Actualisation automatique toutes les 2.5s · Les colis s’affichent en temps réel
            </span>
          </div>
        </div>
      )}

      {/* Sync Failed Banner */}
      {latestSync?.status === 'failed' && (
        <div className="rounded-2xl border border-rust-300 bg-rust-50/90 p-4 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-3 animate-fade">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-xl bg-rust-100 text-rust-700 shrink-0 mt-0.5">
              <AlertTriangle className="h-5 w-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h4 className="text-sm font-bold text-rust-950">
                  Échec de la dernière synchronisation Shiparon
                </h4>
                {latestSync.rowCount > 0 && (
                  <span className="text-[11px] text-rust-800 bg-rust-200/60 px-2 py-0.5 rounded font-mono">
                    {latestSync.rowCount.toLocaleString('fr-FR')} commandes sauvegardées
                  </span>
                )}
              </div>
              <p className="text-xs text-rust-900 mt-1 font-mono break-all bg-rust-100/60 p-2 rounded-lg border border-rust-200">
                {latestSync.errorMessage || 'Une erreur est survenue lors de la synchronisation avec Shiparon.'}
              </p>
              <div className="text-[11px] text-rust-700 mt-1.5">
                Tentative du {formatDateFr(latestSync.createdAt)} ({formatTimeAgo(latestSync.createdAt)})
              </div>
            </div>
          </div>
          <div className="shrink-0 flex items-center gap-2 self-end sm:self-center">
            <Button
              variant="primary"
              size="sm"
              onClick={() => setSyncModalOpen(true)}
              className="bg-rust-700 hover:bg-rust-800 text-white"
            >
              <RefreshCw className="h-4 w-4 mr-1.5" />
              Relancer la synchronisation
            </Button>
          </div>
        </div>
      )}

      {/* SSH Connection Error Banner (if error occurs) */}
      {connTestResult && !connTestResult.ok && (
        <div className="p-3.5 rounded-xl border border-rust-600/30 bg-rust-50 text-rust-900 flex items-center justify-between text-xs">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-rust-600 shrink-0" />
            <span>
              <strong>Échec connexion Shiparon :</strong> {connTestResult.error}
            </span>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setConnTestResult(null)} className="text-xs h-6 px-2">
            Fermer
          </Button>
        </div>
      )}

      {/* High Priority Incident Banner if Ghosted Parcels exist */}
      {ghostedCount > 0 && (
        <div className="p-4 rounded-xl border border-rust-600/30 bg-rust-50/70 text-rust-950 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-xs">
          <div className="flex items-start gap-3">
            <ShieldAlert className="h-6 w-6 text-rust-600 shrink-0 mt-0.5" />
            <div>
              <h3 className="font-bold text-sm sm:text-base text-rust-900">
                Priorité absolue · Argent non récupéré : {ghostedCount} colis sans trace transporteur ({formatTnd(ghostedAmount)})
              </h3>
              <p className="text-xs sm:text-sm text-rust-800/90 mt-0.5 leading-relaxed">
                Ces colis ont été générés sur Shiparon pour {selectedCarrier === 'all' ? 'les transporteurs' : selectedCarrier} mais n'ont
                jamais été enregistrés par le transporteur. Les fichiers de réclamation avec numéros de feuille de route (Runsheet / Décharge) sont prêts.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 shrink-0">
            <a
              href={`/api/v1/shipping/reconciliation/ghosted.xlsx${selectedCarrier !== 'all' ? `?carrier=${encodeURIComponent(selectedCarrier)}` : ''}`}
              download
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-emerald-700 text-white font-medium text-xs hover:bg-emerald-800 shadow-sm transition-colors"
              title="Télécharger la liste au format Excel (.xlsx) avec colonnes Runsheet & montants"
            >
              <FileSpreadsheet className="h-4 w-4" />
              Télécharger Excel (.xlsx)
            </a>
            <a
              href={`/api/v1/shipping/reconciliation/ghosted.csv${selectedCarrier !== 'all' ? `?carrier=${encodeURIComponent(selectedCarrier)}` : ''}`}
              download
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white border border-rust-300 text-rust-900 font-medium text-xs hover:bg-rust-50 shadow-sm transition-colors"
              title="Télécharger la liste au format CSV (.csv) standard"
            >
              <Download className="h-4 w-4" />
              CSV (.csv)
            </a>
          </div>
        </div>
      )}

      {/* Alert for Cancelled Parcels Paid (Fraud Pattern) */}
      {(summary?.cancelledPaidCount ?? 0) > 0 && (
        <div className="p-3.5 rounded-xl border border-rust-600/40 bg-rust-100/80 text-rust-950 flex items-center justify-between text-xs shadow-xs">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-rust-700 shrink-0" />
            <span>
              <strong>Alerte Vol / Fraude :</strong> {summary?.cancelledPaidCount} colis annulés (état 6) ont été déclarés PAYÉS ({formatTnd(summary?.cancelledPaidAmount)}).
            </span>
          </div>
          <button
            type="button"
            onClick={() => {
              setActiveTab('all');
              setSelectedStatus('cancelled');
              setPage(1);
            }}
            className="font-bold underline hover:text-rust-900"
          >
            Examiner les colis annulés payés →
          </button>
        </div>
      )}

      {/* Portview Optimization: Compact KPI Ticker vs Detailed Cards (Req 2) */}
      {compactKpis ? (
        <div className="flex flex-wrap items-center justify-between gap-2 px-3.5 py-2 bg-card rounded-xl border border-border text-xs shadow-2xs animate-fade">
          <div className="flex flex-wrap items-center gap-2 sm:gap-3.5">
            <button
              type="button"
              onClick={() => {
                setActiveTab('ghosted');
                setPage(1);
              }}
              className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md hover:bg-cream/60 transition-colors"
              title="Colis sans trace transporteur"
            >
              <span className="h-2 w-2 rounded-full bg-rust-600 shrink-0" />
              <span className="text-ink-soft">Absents :</span>
              <span className="font-mono font-bold text-rust-700">{ghostedCount.toLocaleString('fr-FR')}</span>
              <span className="text-ink-ghost text-[10px]">({formatTnd(ghostedAmount)})</span>
            </button>

            <span className="text-border hidden sm:inline">|</span>

            <button
              type="button"
              onClick={() => {
                setActiveTab('mismatch');
                setMismatchSubtype('underpaid');
                setPage(1);
              }}
              className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md hover:bg-cream/60 transition-colors"
              title="Colis sous-payés par le transporteur"
            >
              <span className="h-2 w-2 rounded-full bg-clay-600 shrink-0" />
              <span className="text-ink-soft">Déficit :</span>
              <span className="font-mono font-bold text-clay-700">{(summary?.underpaidCount ?? 0).toLocaleString('fr-FR')}</span>
              {summary?.underpaidDeficit ? (
                <span className="text-rust-700 text-[10px]">({formatTnd(summary.underpaidDeficit)})</span>
              ) : null}
            </button>

            <span className="text-border hidden sm:inline">|</span>

            <button
              type="button"
              onClick={() => {
                setActiveTab('all');
                setSelectedStatus('all');
                setPage(1);
              }}
              className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md hover:bg-cream/60 transition-colors"
              title="Total des colis rapprochés"
            >
              <span className="h-2 w-2 rounded-full bg-herb-600 shrink-0" />
              <span className="text-ink-soft">Rapprochés :</span>
              <span className="font-mono font-bold text-ink">{(summary?.matchedCount ?? 0).toLocaleString('fr-FR')}</span>
            </button>

            <span className="text-border hidden sm:inline">|</span>

            <button
              type="button"
              onClick={() => {
                setActiveTab('payments');
                setPaymentStatusFilter('approved');
                setPage(1);
              }}
              className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md hover:bg-cream/60 transition-colors"
              title="Montants encaissés et validés par l'admin"
            >
              <span className="h-2 w-2 rounded-full bg-herb-700 shrink-0" />
              <span className="text-ink-soft">Encaissés :</span>
              <span className="font-mono font-bold text-herb-700">
                {(paymentsSummaryQuery.data?.approvedCount ?? summary?.adminApprovedCount ?? 0).toLocaleString('fr-FR')}
              </span>
              <span className="text-ink-ghost text-[10px]">
                ({formatTnd(paymentsSummaryQuery.data?.approvedAmount ?? summary?.adminApprovedAmount)})
              </span>
            </button>

            <span className="text-border hidden sm:inline">|</span>

            <button
              type="button"
              onClick={() => {
                setActiveTab('unverified');
                setPage(1);
              }}
              className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md hover:bg-cream/60 transition-colors"
              title="En attente de vérification API portail"
            >
              <span className="h-2 w-2 rounded-full bg-amber-500 shrink-0" />
              <span className="text-ink-soft">À vérifier :</span>
              <span className="font-mono font-bold text-amber-800">
                {(summary?.unverifiedCount ?? 0).toLocaleString('fr-FR')}
              </span>
            </button>
          </div>

          <button
            type="button"
            onClick={() => setCompactKpis(false)}
            className="text-[11px] text-ink-soft hover:text-ink font-semibold flex items-center gap-1 px-2 py-1 rounded hover:bg-cream/70 transition-colors ml-auto"
            title="Afficher les 6 cartes d'indicateurs détaillés"
          >
            <span>Détails KPIs</span>
            <ChevronDown className="h-3 w-3" />
          </button>
        </div>
      ) : (
        <div className="space-y-2 animate-fade">
          <div className="flex items-center justify-between">
            <span className="eyebrow text-ink-faint">Indicateurs clés de performance</span>
            <button
              type="button"
              onClick={() => setCompactKpis(true)}
              className="text-xs text-ink-soft hover:text-ink font-semibold flex items-center gap-1 px-2 py-1 rounded hover:bg-cream/70 transition-colors"
              title="Basculer vers le mode portview compact"
            >
              <span>Mode portview compact</span>
              <ChevronDown className="h-3 w-3 rotate-180" />
            </button>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
            <Stat
              label="Argent non récupéré"
              value={ghostedCount.toLocaleString('fr-FR')}
              hint={formatTnd(ghostedAmount)}
              tone={ghostedCount > 0 ? 'alarm' : 'good'}
              onClick={() => {
                setActiveTab('ghosted');
                setPage(1);
              }}
            />
            <Stat
              label="Déficit Sous-payés"
              value={(summary?.underpaidCount ?? 0).toLocaleString('fr-FR')}
              hint={summary?.underpaidDeficit ? `Déficit : ${formatTnd(summary.underpaidDeficit)}` : 'Aucun déficit'}
              tone={(summary?.underpaidCount ?? 0) > 0 ? 'alarm' : 'good'}
              onClick={() => {
                setActiveTab('mismatch');
                setMismatchSubtype('underpaid');
                setPage(1);
              }}
            />
            <Stat
              label="Total Rapprochés"
              value={(summary?.matchedCount ?? 0).toLocaleString('fr-FR')}
              hint={formatTnd(summary?.matchedCarrierAmount)}
              tone="good"
              onClick={() => {
                setActiveTab('all');
                setSelectedStatus('all');
                setPage(1);
              }}
            />
            <Stat
              label="Encaissé & Validé MaPara"
              value={(paymentsSummaryQuery.data?.approvedCount ?? summary?.adminApprovedCount ?? 0).toLocaleString('fr-FR')}
              hint={formatTnd(paymentsSummaryQuery.data?.approvedAmount ?? summary?.adminApprovedAmount)}
              tone="good"
              onClick={() => {
                setActiveTab('payments');
                setPaymentStatusFilter('approved');
                setPage(1);
              }}
            />
            <Stat
              label="Déclaré Payé (Attente Val.)"
              value={(paymentsSummaryQuery.data?.carrierPaidCount ?? summary?.carrierPaidPendingCount ?? 0).toLocaleString('fr-FR')}
              hint={formatTnd(paymentsSummaryQuery.data?.carrierPaidAmount ?? summary?.carrierPaidPendingAmount)}
              tone={(paymentsSummaryQuery.data?.carrierPaidCount ?? summary?.carrierPaidPendingCount) ? 'signal' : 'neutral'}
              onClick={() => {
                setActiveTab('payments');
                setPaymentStatusFilter('carrier_paid');
                setPage(1);
              }}
            />
            <Stat
              label="En attente contrôle API"
              value={(summary?.unverifiedCount ?? 0).toLocaleString('fr-FR')}
              hint={(summary?.unverifiedCount ?? 0) === 0 ? 'Tous interrogés' : 'À interroger via Jetpack API'}
              tone={(summary?.unverifiedCount ?? 0) > 0 ? 'signal' : 'neutral'}
              onClick={() => {
                setActiveTab('unverified');
                setPage(1);
              }}
            />
          </div>
        </div>
      )}

      {/* Tabs & Filter System (Req 1 & Req 3) */}
      <div className="flex flex-col gap-2.5">
        {/* Modern Segmented Navigation Tabs (Req 3 Redesign) */}
        <div className="flex items-center gap-1 bg-cream/70 p-1 rounded-xl border border-border/80 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden shrink-0 shadow-2xs">
          {/* 1. Tous les colis (Default) */}
          <button
            type="button"
            onClick={() => {
              setActiveTab('all');
              setSelectedStatus('all');
              setPage(1);
            }}
            className={`px-3 py-1.5 rounded-lg text-xs transition-all whitespace-nowrap shrink-0 ${
              activeTab === 'all'
                ? 'bg-card text-ink font-bold shadow-xs border border-border/80'
                : 'text-ink-soft font-medium hover:text-ink hover:bg-cream/60'
            }`}
          >
            Tous les colis{parcelsQuery.data?.total ? ` (${parcelsQuery.data.total.toLocaleString('fr-FR')})` : ''}
          </button>

          {/* 2. Absents transporteur */}
          <button
            type="button"
            onClick={() => {
              setActiveTab('ghosted');
              setSelectedStatus('all');
              setPage(1);
            }}
            className={`px-3 py-1.5 rounded-lg text-xs transition-all flex items-center gap-1.5 whitespace-nowrap shrink-0 ${
              activeTab === 'ghosted'
                ? 'bg-card text-ink font-bold shadow-xs border border-border/80'
                : 'text-ink-soft font-medium hover:text-ink hover:bg-cream/60'
            }`}
          >
            <ShieldAlert className="h-3.5 w-3.5 text-rust-600 shrink-0" />
            <span>Absents transporteur</span>
            <span
              className={`px-1.5 py-0.5 rounded-full text-[10px] font-mono font-bold ${
                activeTab === 'ghosted'
                  ? 'bg-rust-50 text-rust-700 border border-rust-200/60'
                  : 'bg-cream text-ink-soft'
              }`}
            >
              {ghostedCount}
            </span>
          </button>

          {/* 3. À vérifier API */}
          <button
            type="button"
            onClick={() => {
              setActiveTab('unverified');
              setSelectedStatus('all');
              setPage(1);
            }}
            className={`px-3 py-1.5 rounded-lg text-xs transition-all flex items-center gap-1.5 whitespace-nowrap shrink-0 ${
              activeTab === 'unverified'
                ? 'bg-card text-ink font-bold shadow-xs border border-border/80'
                : 'text-ink-soft font-medium hover:text-ink hover:bg-cream/60'
            }`}
          >
            <Clock className="h-3.5 w-3.5 text-amber-600 shrink-0" />
            <span>À vérifier API</span>
            <span
              className={`px-1.5 py-0.5 rounded-full text-[10px] font-mono font-bold ${
                activeTab === 'unverified'
                  ? 'bg-amber-50 text-amber-800 border border-amber-200/60'
                  : 'bg-cream text-ink-soft'
              }`}
            >
              {(summary?.unverifiedCount ?? 0).toLocaleString('fr-FR')}
            </span>
          </button>

          {/* 4. Règlements */}
          <button
            type="button"
            onClick={() => {
              setActiveTab('payments');
              setSelectedStatus('all');
              setPage(1);
            }}
            className={`px-3 py-1.5 rounded-lg text-xs transition-all flex items-center gap-1.5 whitespace-nowrap shrink-0 ${
              activeTab === 'payments'
                ? 'bg-card text-ink font-bold shadow-xs border border-border/80'
                : 'text-ink-soft font-medium hover:text-ink hover:bg-cream/60'
            }`}
          >
            <Coins className="h-3.5 w-3.5 text-herb-700 shrink-0" />
            <span>Règlements</span>
            <span
              className={`px-1.5 py-0.5 rounded-full text-[10px] font-mono font-bold ${
                activeTab === 'payments'
                  ? 'bg-herb-50 text-herb-800 border border-herb-200/60'
                  : 'bg-cream text-ink-soft'
              }`}
            >
              {summary?.adminApprovedCount ?? 0} validés
            </span>
          </button>

          {/* 5. Écarts Montant */}
          <button
            type="button"
            onClick={() => {
              setActiveTab('mismatch');
              setMismatchSubtype('all');
              setSelectedStatus('all');
              setPage(1);
            }}
            className={`px-3 py-1.5 rounded-lg text-xs transition-all flex items-center gap-1.5 whitespace-nowrap shrink-0 ${
              activeTab === 'mismatch'
                ? 'bg-card text-ink font-bold shadow-xs border border-border/80'
                : 'text-ink-soft font-medium hover:text-ink hover:bg-cream/60'
            }`}
          >
            <AlertTriangle className="h-3.5 w-3.5 text-clay-600 shrink-0" />
            <span>Écarts Montant</span>
            <span
              className={`px-1.5 py-0.5 rounded-full text-[10px] font-mono font-bold ${
                activeTab === 'mismatch'
                  ? 'bg-clay-50 text-clay-700 border border-clay-200/60'
                  : 'bg-cream text-ink-soft'
              }`}
            >
              {summary?.priceMismatchCount ?? 0}
            </span>
          </button>

          {/* 6. Non assignés */}
          <button
            type="button"
            onClick={() => {
              setActiveTab('unassigned');
              setSelectedStatus('all');
              setPage(1);
            }}
            className={`px-3 py-1.5 rounded-lg text-xs transition-all flex items-center gap-1.5 whitespace-nowrap shrink-0 ${
              activeTab === 'unassigned'
                ? 'bg-card text-ink font-bold shadow-xs border border-border/80'
                : 'text-ink-soft font-medium hover:text-ink hover:bg-cream/60'
            }`}
          >
            <span>Non assignés</span>
            <span
              className={`px-1.5 py-0.5 rounded-full text-[10px] font-mono font-bold ${
                activeTab === 'unassigned'
                  ? 'bg-ink/10 text-ink'
                  : 'bg-cream text-ink-soft'
              }`}
            >
              {(summary?.unassignedCount ?? 0).toLocaleString('fr-FR')}
            </span>
          </button>

          {/* 7. Payments Reconciliation Subpage Link (Req 1) */}
          <Link
            to="/shipping/payments"
            className="px-3 py-1.5 rounded-lg text-xs transition-all flex items-center gap-1.5 whitespace-nowrap shrink-0 text-emerald-800 font-medium hover:bg-emerald-50 border border-emerald-600/20 bg-emerald-50/50"
            title="Ouvrir la page dédiée Imports & Bordereaux de règlement"
          >
            <FileSpreadsheet className="h-3.5 w-3.5 text-emerald-700 shrink-0" />
            <span>Payments Reconciliation</span>
          </Link>
        </div>

        {/* Unified Quick-Filter Toolbar above table (Req 1 & Req 2) */}
        {activeTab !== 'files' && (
          <div className="flex flex-wrap items-center justify-between gap-2.5 p-2 bg-card rounded-xl border border-border shadow-2xs">
            <div className="flex flex-wrap items-center gap-2">
              {/* Search input */}
              <div className="relative">
                <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-ghost" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setPage(1);
                  }}
                  placeholder="Code barre, destinataire, ville, tél…"
                  className="text-xs bg-cream/40 border border-border/80 rounded-lg pl-8 pr-3 py-1.5 text-ink w-44 sm:w-56 focus:outline-none focus:ring-1 focus:ring-clay-600 shadow-2xs"
                />
              </div>

              {/* 1. Transporteur Filter */}
              <div className="flex items-center gap-1 text-xs">
                <select
                  value={selectedCarrier}
                  onChange={(e) => {
                    setSelectedCarrier(e.target.value);
                    setPage(1);
                  }}
                  aria-label="Transporteur"
                  className={`text-xs rounded-lg px-2.5 py-1.5 border font-medium transition-colors shadow-2xs cursor-pointer ${
                    selectedCarrier !== 'all'
                      ? 'bg-clay-50 border-clay-500 text-clay-900 font-bold'
                      : 'bg-cream/40 border-border/80 text-ink-soft hover:text-ink'
                  }`}
                >
                  <option value="all">Tous transporteurs</option>
                  {visibleCarriers.map((c) => (
                    <option key={c.carrier} value={c.rawPresta || 'all'}>
                      {c.carrier} ({c.count.toLocaleString('fr-FR')})
                    </option>
                  ))}
                </select>
              </div>

              {/* 2. Statut Colis Filter */}
              <div className="flex items-center gap-1 text-xs">
                <select
                  value={selectedStatus}
                  onChange={(e) => {
                    const val = e.target.value;
                    setSelectedStatus(val);
                    if (val !== 'all') setActiveTab('all');
                    setPage(1);
                  }}
                  aria-label="Statut Colis"
                  className={`text-xs rounded-lg px-2.5 py-1.5 border font-medium transition-colors shadow-2xs cursor-pointer ${
                    selectedStatus !== 'all'
                      ? 'bg-clay-50 border-clay-500 text-clay-900 font-bold'
                      : 'bg-cream/40 border-border/80 text-ink-soft hover:text-ink'
                  }`}
                >
                  <option value="all">Tous statuts colis</option>
                  <option value="ghosted">Absent transporteur</option>
                  <option value="unverified">À vérifier API</option>
                  <option value="reconciled_paid">Livré & Payé</option>
                  <option value="reconciled_delivered">Livré (Non payé)</option>
                  <option value="reconciled_in_transit">En cours transporteur</option>
                  <option value="reconciled_return">Retour</option>
                  <option value="price_mismatch">Écart de prix</option>
                  <option value="unassigned">Non assigné</option>
                  <option value="cancelled">Annulé (état 6)</option>
                </select>
              </div>

              {/* 3. Date Range Filter */}
              <div className="flex items-center gap-1 text-xs">
                <select
                  value={datePreset}
                  onChange={(e) => {
                    setDatePreset(e.target.value as 'all' | 'today' | '7d' | '30d' | 'custom');
                    setPage(1);
                  }}
                  aria-label="Date"
                  className={`text-xs rounded-lg px-2.5 py-1.5 border font-medium transition-colors shadow-2xs cursor-pointer ${
                    datePreset !== 'all'
                      ? 'bg-clay-50 border-clay-500 text-clay-900 font-bold'
                      : 'bg-cream/40 border-border/80 text-ink-soft hover:text-ink'
                  }`}
                >
                  <option value="all">Toutes dates</option>
                  <option value="today">Aujourd'hui</option>
                  <option value="7d">7 derniers jours</option>
                  <option value="30d">30 derniers jours</option>
                  <option value="custom">Date personnalisée…</option>
                </select>

                {datePreset === 'custom' && (
                  <div className="flex items-center gap-1 ml-1 animate-fade">
                    <input
                      type="date"
                      value={customStartDate}
                      onChange={(e) => { setCustomStartDate(e.target.value); setPage(1); }}
                      className="text-xs bg-card border border-border rounded-lg px-2 py-1 text-ink"
                    />
                    <span className="text-ink-ghost text-xs">à</span>
                    <input
                      type="date"
                      value={customEndDate}
                      onChange={(e) => { setCustomEndDate(e.target.value); setPage(1); }}
                      className="text-xs bg-card border border-border rounded-lg px-2 py-1 text-ink"
                    />
                  </div>
                )}
              </div>

              {/* 4. État Shiparon Filter */}
              <div className="flex items-center gap-1 text-xs">
                <select
                  value={selectedShiparonEtat}
                  onChange={(e) => {
                    setSelectedShiparonEtat(e.target.value);
                    setPage(1);
                  }}
                  aria-label="État Shiparon"
                  className={`text-xs rounded-lg px-2.5 py-1.5 border font-medium transition-colors shadow-2xs cursor-pointer ${
                    selectedShiparonEtat !== 'all'
                      ? 'bg-clay-50 border-clay-500 text-clay-900 font-bold'
                      : 'bg-cream/40 border-border/80 text-ink-soft hover:text-ink'
                  }`}
                >
                  <option value="all">Tous états Shiparon</option>
                  <option value="0">0 - En attente (En entrepôt)</option>
                  <option value="1">1 - En cours</option>
                  <option value="2">2 - Livré</option>
                  <option value="3">3 - Échange</option>
                  <option value="4">4 - Transféré</option>
                  <option value="5">5 - Rtn expéditeur</option>
                  <option value="6">6 - Annulé / Supprimé</option>
                  <option value="7">7 - Rtn client / agence</option>
                  <option value="8">8 - Au magasin / dépôt</option>
                  <option value="9">9 - Inter dépôt</option>
                  <option value="10">10 - Livré payé</option>
                  <option value="11">11 - Rtn dépôt</option>
                  <option value="12">12 - À enlever</option>
                  <option value="13">13 - Enlevé</option>
                  <option value="15">15 - Reçu en agence</option>
                  <option value="16">16 - Ramassage</option>
                  <option value="20">20 - À vérifier</option>
                  <option value="30">30 - Rtn reçu (Stock)</option>
                  <option value="31">31 - Rtn définitif</option>
                  <option value="32">32 - Reçu payé</option>
                  <option value="99">99 - Non reçu</option>
                </select>
              </div>

              {/* Payment filter when on payments tab */}
              {activeTab === 'payments' && (
                <div className="flex items-center gap-1 text-xs">
                  <select
                    value={paymentStatusFilter}
                    onChange={(e) => {
                      setPaymentStatusFilter(e.target.value as 'all' | 'unpaid' | 'carrier_paid' | 'approved');
                      setPage(1);
                    }}
                    className="text-xs bg-cream/40 border border-border/80 rounded-lg px-2.5 py-1.5 text-ink font-medium shadow-2xs cursor-pointer"
                  >
                    <option value="all">Tous statuts financiers</option>
                    <option value="carrier_paid">En attente validation admin</option>
                    <option value="approved">Encaissé & Validé MaPara</option>
                    <option value="unpaid">Non payé</option>
                  </select>
                </div>
              )}
            </div>

            {/* Clear filters button & Density Toggle */}
            <div className="flex items-center gap-2 ml-auto">
              {activeFiltersCount > 0 && (
                <button
                  type="button"
                  onClick={resetAllFilters}
                  className="text-xs text-rust-700 hover:text-rust-900 bg-rust-50 hover:bg-rust-100 border border-rust-200 px-2.5 py-1 rounded-lg font-medium flex items-center gap-1 transition-colors"
                >
                  <RotateCcw className="h-3 w-3" />
                  <span>Effacer filtres ({activeFiltersCount})</span>
                </button>
              )}

              <button
                type="button"
                onClick={() => setCompactDensity((prev) => !prev)}
                className={`text-xs px-2.5 py-1 rounded-lg border font-medium flex items-center gap-1 transition-colors ${
                  compactDensity
                    ? 'bg-card border-border text-ink'
                    : 'bg-cream/40 border-border/80 text-ink-soft hover:text-ink'
                }`}
                title="Basculer entre affichage compact haute-densité et affichage standard"
              >
                <SlidersHorizontal className="h-3 w-3" />
                <span className="hidden sm:inline">{compactDensity ? 'Dense' : 'Aéré'}</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Main Content Area */}
      {activeTab === 'files' ? (
        /* Imports & Settlements History */
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Upload Delivery CSV */}
            <Panel className="p-5 border-border bg-card shadow-xs">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-base font-bold text-ink">Fichier de Dépose Transporteur</h3>
                  <p className="text-xs text-ink-soft mt-1 leading-relaxed">
                    Importez le fichier CSV transmis par le transporteur (ex: <code>commande-mapara.csv</code>) pour
                    détecter les colis fantômes et les écarts de prix COD.
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadDeliveryMutation.isPending}
                >
                  <Upload className="h-4 w-4 mr-1 text-ink-soft" />
                  {uploadDeliveryMutation.isPending ? 'Importation…' : 'Importer CSV Dépose'}
                </Button>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept=".csv,.txt"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) uploadDeliveryMutation.mutate(f);
                  }}
                />
              </div>
            </Panel>

            {/* Upload Settlement CSV */}
            <Panel className="p-5 border-border bg-card shadow-xs">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-base font-bold text-ink">Bordereau de Règlement Financier</h3>
                  <p className="text-xs text-ink-soft mt-1 leading-relaxed">
                    Importez les fichiers de recette quotidiens (ex: <code>detail_recette_YYYY-MM-DD.csv</code>) pour
                    rapprocher les montants effectivement versés sur votre compte bancaire.
                  </p>
                </div>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => settlementFileInputRef.current?.click()}
                  disabled={uploadSettlementMutation.isPending}
                >
                  <Upload className="h-4 w-4 mr-1" />
                  {uploadSettlementMutation.isPending ? 'Importation…' : 'Importer Bordereau Recette'}
                </Button>
                <input
                  type="file"
                  ref={settlementFileInputRef}
                  accept=".csv,.txt"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) uploadSettlementMutation.mutate(f);
                  }}
                />
              </div>
            </Panel>
          </div>

          {/* Settlements Table */}
          <Panel className="p-5 border-border bg-card shadow-xs">
            <h3 className="text-base font-bold text-ink mb-1">Bordereaux de Règlement Enregistrés</h3>
            <p className="text-xs text-ink-soft mb-4">
              Chaque bordereau correspond à un versement financier déclaré par le transporteur.
            </p>
            <TableShell>
              <Table>
                <THead>
                  <Tr>
                    <Th>Date Règlement</Th>
                    <Th>Fichier</Th>
                    <Th>Transporteur</Th>
                    <Th align="right">Montant Total</Th>
                    <Th align="right">Colis</Th>
                    <Th>Statut Validation</Th>
                    <Th align="right">Action Admin</Th>
                  </Tr>
                </THead>
                <TBody>
                  {(settlementsQuery.data?.settlements ?? []).map((s) => (
                    <Tr key={s.id}>
                      <Td>
                        <span className="font-mono text-xs font-semibold text-ink">
                          {s.settlementDate ? s.settlementDate.split('T')[0] : '—'}
                        </span>
                      </Td>
                      <Td>
                        <span className="font-mono text-xs text-ink-soft">{s.filename || '—'}</span>
                      </Td>
                      <Td>
                        <span className="text-xs font-medium text-ink">{s.carrierName}</span>
                      </Td>
                      <Td align="right">
                        <span className="font-mono text-xs font-bold text-ink">{formatTnd(s.totalAmount)}</span>
                      </Td>
                      <Td align="right">
                        <span className="font-mono text-xs text-ink">{s.parcelCount}</span>
                      </Td>
                      <Td>
                        {s.status === 'approved' ? (
                          <Badge tone="good">✓ Encaissé & Validé</Badge>
                        ) : (
                          <Badge tone="signal">En attente approbation</Badge>
                        )}
                      </Td>
                      <Td align="right">
                        {s.status !== 'approved' && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-xs py-1 text-herb-800 border-herb-600/40 hover:bg-herb-50"
                            onClick={() => {
                              setTargetSettlementId(s.id);
                              setApprovalModalOpen(true);
                            }}
                          >
                            <CheckCheck className="h-3.5 w-3.5 mr-1 text-herb-700" />
                            Valider le lot
                          </Button>
                        )}
                      </Td>
                    </Tr>
                  ))}
                  {(settlementsQuery.data?.settlements ?? []).length === 0 && (
                    <Tr>
                      <Td colSpan={7} className="text-center py-6 text-ink-ghost text-xs">
                        Aucun bordereau de règlement importé pour le moment.
                      </Td>
                    </Tr>
                  )}
                </TBody>
              </Table>
            </TableShell>
          </Panel>

          {/* Sync History */}
          <Panel className="p-5 border-border bg-card shadow-xs">
            <h3 className="text-base font-bold text-ink mb-1">Historique des synchronisations</h3>
            <p className="text-xs text-ink-soft mb-4">
              Connexion SSH MySQL en lecture seule vers le serveur Shiparon
            </p>
            <TableShell>
              <Table>
                <THead>
                  <Tr>
                    <Th>ID</Th>
                    <Th>Déclencheur</Th>
                    <Th>Période</Th>
                    <Th align="right">Commandes synchronisées</Th>
                    <Th align="right">Durée</Th>
                    <Th>Statut</Th>
                    <Th>Date</Th>
                  </Tr>
                </THead>
                <TBody>
                  {(historyQuery.data?.syncs ?? []).map((s) => (
                    <Tr key={s.id}>
                      <Td>
                        <span className="font-mono text-xs">#{String(s.id)}</span>
                      </Td>
                      <Td>
                        <span className="text-xs text-ink-soft">{s.triggeredBy ?? 'système'}</span>
                      </Td>
                      <Td>
                        <span className="text-xs text-ink-soft font-mono">
                          {s.periodStart ? s.periodStart.split('T')[0] : '2026-01-01'}
                        </span>
                      </Td>
                      <Td align="right">
                        <span className="font-mono text-xs font-semibold text-ink">
                          {s.rowCount?.toLocaleString('fr-FR')}
                        </span>
                      </Td>
                      <Td align="right">
                        <span className="font-mono text-xs text-ink-soft">
                          {s.durationMs ? (
                            s.durationMs >= 60000
                              ? `${Math.floor(s.durationMs / 60000)}m ${Math.round((s.durationMs % 60000) / 1000)}s`
                              : `${(s.durationMs / 1000).toFixed(1)}s`
                          ) : s.status === 'running' ? (
                            <span className="text-amber-700 italic text-[11px]">En cours…</span>
                          ) : (
                            '—'
                          )}
                        </span>
                      </Td>
                      <Td>
                        {s.status === 'completed' && <Badge tone="good">Terminé</Badge>}
                        {s.status === 'running' && (
                          <div className="space-y-1">
                            <Badge tone="scan" className="animate-pulse">
                              <RefreshCw className="h-3 w-3 mr-1 animate-spin" /> En cours
                            </Badge>
                            {s.progressStage && (
                              <div className="text-[10px] text-amber-800 font-medium max-w-xs truncate" title={s.progressStage}>
                                {s.progressStage}
                              </div>
                            )}
                          </div>
                        )}
                        {s.status === 'failed' && (
                          <div className="space-y-1">
                            <Badge tone="alarm">Échec</Badge>
                            {(s.errorMessage || s.error) && (
                              <div className="text-[10px] text-rust-700 max-w-xs truncate" title={s.errorMessage || s.error || ''}>
                                {s.errorMessage || s.error}
                              </div>
                            )}
                          </div>
                        )}
                      </Td>
                      <Td>
                        <span className="text-xs text-ink-faint">
                          {s.createdAt ? new Date(s.createdAt).toLocaleString('fr-FR') : '—'}
                        </span>
                      </Td>
                    </Tr>
                  ))}
                </TBody>
              </Table>
            </TableShell>
          </Panel>
        </div>
      ) : (
        /* Parcels Table Views (Ghosted, Unverified, Mismatch, Payments, Unassigned, All) */
        <div className="space-y-4">
          {/* Explanation Banner in "À vérifier API" Tab */}
          {activeTab === 'unverified' && (
            <div className="p-4 rounded-xl border border-amber-500/30 bg-amber-50/70 text-amber-950 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-xs">
              <div className="flex items-start gap-3">
                <Clock className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-bold text-sm text-amber-950">
                    File d'attente d'interrogation API transporteur ({(summary?.unverifiedCount ?? 0).toLocaleString('fr-FR')} colis)
                  </h3>
                  <p className="text-xs text-amber-900/90 mt-0.5 leading-relaxed">
                    <strong>Pourquoi ces colis sont « En attente API » ?</strong> Ils sont créés dans Shiparon mais n'ont pas encore été interrogés
                    auprès de l'API transporteur en direct. Cliquez ci-contre pour interroger l'API Jetpack v2 et actualiser leurs statuts réels.
                  </p>
                  {jetpackPollingActive && (
                    <div className="mt-2 inline-flex items-center gap-2 text-xs font-semibold text-amber-800 bg-amber-100/90 px-2.5 py-1 rounded-md animate-pulse">
                      <Sparkles className="h-3.5 w-3.5 animate-spin text-amber-700" />
                      Interrogation en direct auprès de Jetpack API v2 en cours... Les compteurs s'actualisent en temps réel.
                    </div>
                  )}
                </div>
              </div>
              {(selectedCarrier === 'all' || selectedCarrier.toLowerCase().includes('jetpack')) && (
                <Button
                  variant="primary"
                  size="sm"
                  className="shrink-0 bg-amber-600 hover:bg-amber-700 text-white border-none shadow-xs font-semibold"
                  disabled={syncJetpackTrackMutation.isPending || jetpackPollingActive}
                  onClick={() => syncJetpackTrackMutation.mutate({ limit: 25000, unverifiedOnly: true })}
                >
                  <Sparkles className={`h-4 w-4 mr-1.5 ${syncJetpackTrackMutation.isPending || jetpackPollingActive ? 'animate-spin' : ''}`} />
                  {syncJetpackTrackMutation.isPending || jetpackPollingActive ? 'Vérification en cours…' : 'Lancer vérification API Jetpack'}
                </Button>
              )}
            </div>
          )}

          {/* Explanation Banner in "Non assignés" Tab */}
          {activeTab === 'unassigned' && (
            <div className="p-4 rounded-xl border border-border bg-card text-ink flex items-start gap-3 shadow-xs">
              <AlertTriangle className="h-5 w-5 text-clay-600 shrink-0 mt-0.5" />
              <div>
                <h3 className="font-bold text-sm text-ink">
                  Commandes sans transporteur assigné ({(summary?.unassignedCount ?? 0).toLocaleString('fr-FR')} commandes)
                </h3>
                <p className="text-xs text-ink-soft mt-0.5 leading-relaxed">
                  Ces commandes sont enregistrées dans Shiparon sans qu'un prestataire de livraison (Jetpack, etc.) n'ait été spécifié dans le champ prestataire.
                </p>
              </div>
            </div>
          )}

          {/* Double Verification Banner in Payments Tab */}
          {activeTab === 'payments' && (
            <div className="p-4 rounded-xl border border-herb-600/30 bg-herb-50/50 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex items-start gap-3">
                <CreditCard className="h-6 w-6 text-herb-700 shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-sm font-bold text-herb-900">
                    Double niveau de vérification financière & validation d'encaissement
                  </h3>
                  <p className="text-xs text-herb-800/90 mt-0.5 leading-relaxed">
                    <strong>Niveau 1 :</strong> Statut déclaré par le transporteur (API v2 / bordereau).
                    <br />
                    <strong>Niveau 2 :</strong> Fichiers de règlement (bordereaux de recette bancaires).
                    <br />
                    <strong>Niveau 3 :</strong> Approbation administrateur obligatoire attestant l'encaissement effectif sur le compte bancaire MaPara.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => settlementFileInputRef.current?.click()}
                >
                  <Upload className="h-4 w-4 mr-1.5" />
                  Importer Bordereau Recette
                </Button>
              </div>
            </div>
          )}

          {/* Sub-filters in "Écarts Montant" Tab */}
          {activeTab === 'mismatch' && (
            <div className="p-3.5 bg-card rounded-xl border border-border shadow-xs flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-bold text-ink-soft uppercase tracking-wider px-1">Filtre écart :</span>
                <button
                  type="button"
                  onClick={() => {
                    setMismatchSubtype('all');
                    setPage(1);
                  }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 ${
                    mismatchSubtype === 'all'
                      ? 'bg-clay-700 text-white shadow-xs'
                      : 'bg-cream/60 text-ink-soft hover:text-ink hover:bg-cream border border-border/70'
                  }`}
                >
                  <span>Tous les écarts</span>
                  <span
                    className={`text-[10px] px-1.5 py-0.2 rounded-full font-mono ${
                      mismatchSubtype === 'all' ? 'bg-white/20 text-white' : 'bg-cream text-ink-soft'
                    }`}
                  >
                    {summary?.priceMismatchCount ?? 0}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setMismatchSubtype('underpaid');
                    setPage(1);
                  }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 ${
                    mismatchSubtype === 'underpaid'
                      ? 'bg-rust-600 text-white shadow-xs'
                      : 'bg-cream/60 text-ink-soft hover:text-ink hover:bg-cream border border-border/70'
                  }`}
                >
                  <TrendingDown className="h-3.5 w-3.5" />
                  <span>Sous-payés (Déficit : {formatTnd(summary?.underpaidDeficit)})</span>
                  <span
                    className={`text-[10px] px-1.5 py-0.2 rounded-full font-mono ${
                      mismatchSubtype === 'underpaid' ? 'bg-white/20 text-white' : 'bg-rust-100 text-rust-800'
                    }`}
                  >
                    {summary?.underpaidCount ?? 0}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setMismatchSubtype('overpaid');
                    setPage(1);
                  }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 ${
                    mismatchSubtype === 'overpaid'
                      ? 'bg-herb-700 text-white shadow-xs'
                      : 'bg-cream/60 text-ink-soft hover:text-ink hover:bg-cream border border-border/70'
                  }`}
                >
                  <TrendingUp className="h-3.5 w-3.5" />
                  <span>Sur-payés (Surplus : {formatTnd(summary?.overpaidSurplus)})</span>
                  <span
                    className={`text-[10px] px-1.5 py-0.2 rounded-full font-mono ${
                      mismatchSubtype === 'overpaid' ? 'bg-white/20 text-white' : 'bg-herb-100 text-herb-800'
                    }`}
                  >
                    {summary?.overpaidCount ?? 0}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setMismatchSubtype('exact');
                    setPage(1);
                  }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 ${
                    mismatchSubtype === 'exact'
                      ? 'bg-ink text-white shadow-xs'
                      : 'bg-cream/60 text-ink-soft hover:text-ink hover:bg-cream border border-border/70'
                  }`}
                >
                  <span>Exacts</span>
                  <span
                    className={`text-[10px] px-1.5 py-0.2 rounded-full font-mono ${
                      mismatchSubtype === 'exact' ? 'bg-white/20 text-white' : 'bg-cream text-ink-soft'
                    }`}
                  >
                    {summary?.exactPaidCount ?? 0}
                  </span>
                </button>
              </div>
              <div className="text-xs text-ink-soft">
                Tolérance stricte : <span className="font-mono font-semibold">±0.010 DT</span>
              </div>
            </div>
          )}

          {parcelsQuery.isLoading ? (
            <div className="p-12 flex justify-center">
              <Loading />
            </div>
          ) : (
            <>
              <TableShell className="max-h-[calc(100vh-270px)] overflow-y-auto border border-border rounded-xl shadow-2xs">
                <Table>
                  <THead className="sticky top-0 bg-card z-10 shadow-2xs">
                    <Tr>
                      <SortTh
                        field="code"
                        label="Code barre"
                        currentField={sortField}
                        direction={sortDirection}
                        onSort={handleSort}
                      />

                      {/* 1. Statut Colis - Filtrable (Req 1) */}
                      <Th>
                        <div className="flex items-center gap-1.5">
                          <span className="font-semibold text-xs whitespace-nowrap">Statut Colis</span>
                          <select
                            value={selectedStatus}
                            onChange={(e) => {
                              const val = e.target.value;
                              setSelectedStatus(val);
                              if (val !== 'all') setActiveTab('all');
                              setPage(1);
                            }}
                            aria-label="Filtrer par statut colis"
                            className={`text-[11px] font-medium rounded px-1.5 py-0.5 border cursor-pointer transition-colors ${
                              selectedStatus !== 'all'
                                ? 'bg-clay-50 border-clay-500 text-clay-900 font-bold'
                                : 'bg-cream/40 border-border/80 text-ink-soft hover:text-ink'
                            }`}
                          >
                            <option value="all">Tous</option>
                            <option value="ghosted">Absent</option>
                            <option value="unverified">À vérifier</option>
                            <option value="reconciled_paid">Livré & Payé</option>
                            <option value="reconciled_delivered">Livré (Non payé)</option>
                            <option value="reconciled_in_transit">En cours</option>
                            <option value="reconciled_return">Retour</option>
                            <option value="price_mismatch">Écart prix</option>
                            <option value="unassigned">Non assigné</option>
                            <option value="cancelled">Annulé</option>
                          </select>
                        </div>
                      </Th>

                      {/* 2. Transporteur - Filtrable (Req 1) */}
                      <Th>
                        <div className="flex items-center gap-1.5">
                          <span className="font-semibold text-xs whitespace-nowrap">Transporteur</span>
                          <select
                            value={selectedCarrier}
                            onChange={(e) => {
                              setSelectedCarrier(e.target.value);
                              setPage(1);
                            }}
                            aria-label="Filtrer par transporteur"
                            className={`text-[11px] font-medium rounded px-1.5 py-0.5 border cursor-pointer transition-colors ${
                              selectedCarrier !== 'all'
                                ? 'bg-clay-50 border-clay-500 text-clay-900 font-bold'
                                : 'bg-cream/40 border-border/80 text-ink-soft hover:text-ink'
                            }`}
                          >
                            <option value="all">Tous</option>
                            {visibleCarriers.map((c) => (
                              <option key={c.carrier} value={c.rawPresta || 'all'}>
                                {c.carrier}
                              </option>
                            ))}
                          </select>
                        </div>
                      </Th>

                      {/* 3. Date - Triable & Filtrable (Req 1) */}
                      <Th>
                        <div className="flex items-center gap-1.5">
                          <SortButton
                            field="date"
                            label="Date"
                            currentField={sortField}
                            direction={sortDirection}
                            onSort={handleSort}
                          />
                          <select
                            value={datePreset}
                            onChange={(e) => {
                              setDatePreset(e.target.value as 'all' | 'today' | '7d' | '30d' | 'custom');
                              setPage(1);
                            }}
                            aria-label="Filtrer par date"
                            className={`text-[11px] font-medium rounded px-1 py-0.5 border cursor-pointer transition-colors ${
                              datePreset !== 'all'
                                ? 'bg-clay-50 border-clay-500 text-clay-900 font-bold'
                                : 'bg-cream/40 border-border/80 text-ink-soft hover:text-ink'
                            }`}
                          >
                            <option value="all">Toutes</option>
                            <option value="today">Aujourd'hui</option>
                            <option value="7d">7j</option>
                            <option value="30d">30j</option>
                            <option value="custom">Date…</option>
                          </select>
                        </div>
                      </Th>

                      <Th>Destinataire</Th>
                      <Th>Destination</Th>
                      <SortTh
                        field="price"
                        label="COD Attendu"
                        align="right"
                        currentField={sortField}
                        direction={sortDirection}
                        onSort={handleSort}
                      />
                      {activeTab === 'payments' ? (
                        <>
                          <Th>Statut Transporteur</Th>
                          <Th>Validation Encaissement</Th>
                          <Th align="right">Action Admin</Th>
                        </>
                      ) : (
                        <>
                          <Th align="right">Rapprochement</Th>
                          {/* 4. État Shiparon - Filtrable (Req 1) */}
                          <Th>
                            <div className="flex items-center gap-1.5">
                              <span className="font-semibold text-xs whitespace-nowrap">État Shiparon</span>
                              <select
                                value={selectedShiparonEtat}
                                onChange={(e) => {
                                  setSelectedShiparonEtat(e.target.value);
                                  setPage(1);
                                }}
                                aria-label="Filtrer par état Shiparon"
                                className={`text-[11px] font-medium rounded px-1 py-0.5 border cursor-pointer transition-colors ${
                                  selectedShiparonEtat !== 'all'
                                    ? 'bg-clay-50 border-clay-500 text-clay-900 font-bold'
                                    : 'bg-cream/40 border-border/80 text-ink-soft hover:text-ink'
                                }`}
                              >
                                <option value="all">Tous</option>
                                <option value="0">0 - En attente</option>
                                <option value="1">1 - En cours</option>
                                <option value="2">2 - Livré</option>
                                <option value="3">3 - Échange</option>
                                <option value="4">4 - Transféré</option>
                                <option value="5">5 - Rtn expéditeur</option>
                                <option value="6">6 - Annulé</option>
                                <option value="7">7 - Rtn agence</option>
                                <option value="8">8 - Magasin</option>
                                <option value="9">9 - Inter dépôt</option>
                                <option value="10">10 - Livré payé</option>
                                <option value="11">11 - Rtn dépôt</option>
                                <option value="15">15 - Reçu agence</option>
                                <option value="16">16 - Ramassage</option>
                                <option value="20">20 - À vérifier</option>
                                <option value="30">30 - Rtn reçu</option>
                                <option value="99">99 - Non reçu</option>
                              </select>
                            </div>
                          </Th>
                        </>
                      )}
                    </Tr>
                  </THead>
                  <TBody>
                    {sortedParcels.map((p: ReconciledParcelItem) => (
                      <Tr
                        key={p.codeBarre}
                        onClick={() => setSelectedParcelDetail(p)}
                        className="cursor-pointer hover:bg-cream/40 transition-colors"
                      >
                        <Td>
                          <div className="flex items-center gap-1.5">
                            <span className="font-mono text-xs font-semibold text-ink">
                              {p.codeBarre}
                            </span>
                            <button
                              type="button"
                              title="Copier le code barre"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigator.clipboard.writeText(p.codeBarre);
                                setCopiedBarcode(p.codeBarre);
                                setTimeout(() => setCopiedBarcode(null), 1500);
                              }}
                              className="text-ink-ghost hover:text-ink p-0.5 rounded transition-colors"
                            >
                              {copiedBarcode === p.codeBarre ? (
                                <Check className="h-3 w-3 text-herb-700" />
                              ) : (
                                <Copy className="h-3 w-3" />
                              )}
                            </button>
                          </div>
                          {p.idRunsheet ? (
                            <div className="flex items-center gap-1 mt-1">
                              <Link
                                to={`/shipping/runsheets/${p.idRunsheet}`}
                                onClick={(e) => e.stopPropagation()}
                                className="inline-flex items-center gap-1 text-[10px] font-mono font-bold text-clay-700 hover:text-clay-900 bg-clay-50 hover:bg-clay-100 px-1.5 py-0.5 rounded border border-clay-300/60 transition-colors"
                                title={`Feuille de route #${p.idRunsheet} (Cliquer pour ouvrir la tournée)`}
                              >
                                <Route className="h-2.5 w-2.5 text-clay-600" />
                                #{p.idRunsheet}
                              </Link>
                              <button
                                type="button"
                                title="Copier le N° de runsheet"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigator.clipboard.writeText(String(p.idRunsheet));
                                  setCopiedBarcode(`rs-${p.idRunsheet}`);
                                  setTimeout(() => setCopiedBarcode(null), 1500);
                                }}
                                className="text-ink-ghost hover:text-ink p-0.5 rounded transition-colors"
                              >
                                {copiedBarcode === `rs-${p.idRunsheet}` ? (
                                  <Check className="h-2.5 w-2.5 text-herb-700" />
                                ) : (
                                  <Copy className="h-2.5 w-2.5" />
                                )}
                              </button>
                              {p.runsheetDate && (
                                <span className="text-[10px] text-ink-faint">
                                  {formatDateFr(p.runsheetDate)}
                                </span>
                              )}
                            </div>
                          ) : p.status === 'ghosted' || activeTab === 'ghosted' ? (
                            <div className="text-[10px] text-rust-600/80 italic mt-0.5">
                              Sans runsheet
                            </div>
                          ) : null}
                        </Td>
                        <Td>{getStatusBadge(p.status, p.mismatchType, p.priceDiff)}</Td>
                        <Td>
                          {p.carrier ? (
                            <span className="text-xs font-medium text-ink whitespace-nowrap">
                              {p.carrier}
                            </span>
                          ) : (
                            <span className="text-xs text-ink-ghost italic whitespace-nowrap">
                              Non assigné
                            </span>
                          )}
                        </Td>
                        <Td>
                          <span className="text-xs text-ink-soft whitespace-nowrap">
                            {formatDateFr(p.date)}
                          </span>
                        </Td>
                        <Td>
                          <div className="text-xs font-medium text-ink truncate max-w-[180px]">
                            {p.clientName || '—'}
                          </div>
                          {p.clientPhone && (
                            <a
                              href={`tel:${p.clientPhone.replace(/\D/g, '')}`}
                              onClick={(e) => e.stopPropagation()}
                              className="inline-flex items-center gap-1 text-[11px] text-ink-soft hover:text-clay-700 font-mono transition-colors"
                              title="Appeler le client"
                            >
                              <Phone className="h-3 w-3 text-ink-ghost" />
                              {formatTunisianPhone(p.clientPhone)}
                            </a>
                          )}
                        </Td>
                        <Td>
                          <span className="text-xs text-ink-soft truncate max-w-[150px] block">
                            {p.location || '—'}
                          </span>
                        </Td>
                        <Td align="right">
                          <span className="font-mono text-xs font-semibold text-ink">
                            {p.shiparonPrice !== null ? formatTnd(p.shiparonPrice) : '—'}
                          </span>
                        </Td>

                        {activeTab === 'payments' ? (
                          <>
                            <Td>
                              <span className="text-xs text-ink-soft whitespace-nowrap">
                                {p.carrierStatus || (p.carrierPaye === 1 ? 'Payé' : 'Non vérifié')}
                              </span>
                            </Td>
                            <Td>{getPaymentBadge(p.adminPaymentStatus)}</Td>
                            <Td align="right">
                              {p.adminPaymentStatus === 'approved' ? (
                                <span className="text-[11px] text-herb-700 font-medium whitespace-nowrap">✓ Validé</span>
                              ) : (
                                // validation is PER PAYMENT FILE (client rule 2026-10-04):
                                // validate the bordereau in the Fichiers list, never a single parcel
                                <span className="text-[10px] text-ink-ghost whitespace-nowrap">
                                  {p.settlementBatch ? `lot ${p.settlementBatch}` : '—'}
                                </span>
                              )}
                            </Td>
                          </>
                        ) : (
                          <>
                            <Td align="right">
                              {p.carrierPrice !== null ? (
                                <div className="flex flex-col items-end">
                                  <span className="font-mono text-xs font-semibold text-ink">
                                    {formatTnd(p.carrierPrice)}
                                  </span>
                                  {p.priceDiff !== null && Math.abs(p.priceDiff) > 0.01 ? (
                                    <span
                                      className={`inline-flex items-center gap-0.5 text-[10px] font-mono font-bold ${
                                        p.priceDiff < 0 ? 'text-rust-600' : 'text-herb-700'
                                      }`}
                                    >
                                      {p.priceDiff < 0 ? (
                                        <>
                                          <TrendingDown className="h-2.5 w-2.5" />
                                          Déficit {formatTnd(Math.abs(p.priceDiff))}
                                        </>
                                      ) : (
                                        <>
                                          <TrendingUp className="h-2.5 w-2.5" />
                                          +{formatTnd(p.priceDiff)}
                                        </>
                                      )}
                                    </span>
                                  ) : (
                                    <span className="text-[10px] font-mono text-herb-700">Conforme</span>
                                  )}
                                </div>
                              ) : (
                                <span className="text-xs text-ink-ghost font-mono">—</span>
                              )}
                            </Td>
                            <Td>
                              <span className="text-xs text-ink-soft whitespace-nowrap">
                                {p.shiparonEtatLabel || '—'}
                              </span>
                            </Td>
                          </>
                        )}
                      </Tr>
                    ))}
                    {sortedParcels.length === 0 && (
                      <Tr>
                        <Td
                          colSpan={activeTab === 'payments' ? 10 : 9}
                          className="text-center py-12 text-ink-ghost text-xs"
                        >
                          {activeTab === 'ghosted' ? (
                            <div className="flex flex-col items-center justify-center gap-2 text-ink-soft py-4">
                              <div className="p-3 bg-herb-50 rounded-full border border-herb-600/20 text-herb-700">
                                <CheckCheck className="h-6 w-6" />
                              </div>
                              <p className="font-semibold text-sm text-ink">Aucun colis fantôme détecté</p>
                              <p className="text-xs text-ink-faint max-w-md">
                                Tous les colis créés sur Shiparon sont reconnus par le transporteur ou résolus via les fichiers de paiement.
                              </p>
                            </div>
                          ) : activeTab === 'unverified' ? (
                            <div className="flex flex-col items-center justify-center gap-2 text-ink-soft py-4">
                              <div className="p-3 bg-herb-50 rounded-full border border-herb-600/20 text-herb-700">
                                <CheckCircle2 className="h-6 w-6" />
                              </div>
                              <p className="font-semibold text-sm text-ink">Tous les colis ont été vérifiés</p>
                              <p className="text-xs text-ink-faint max-w-md">
                                Aucun colis en attente d'interrogation API auprès des transporteurs.
                              </p>
                            </div>
                          ) : activeTab === 'unassigned' ? (
                            <div className="flex flex-col items-center justify-center gap-2 text-ink-soft py-4">
                              <div className="p-3 bg-herb-50 rounded-full border border-herb-600/20 text-herb-700">
                                <CheckCircle2 className="h-6 w-6" />
                              </div>
                              <p className="font-semibold text-sm text-ink">Aucune commande non assignée</p>
                              <p className="text-xs text-ink-faint max-w-md">
                                Toutes les commandes ont un transporteur désigné dans Shiparon.
                              </p>
                            </div>
                          ) : activeTab === 'mismatch' ? (
                            <div className="flex flex-col items-center justify-center gap-2 text-ink-soft py-4">
                              <div className="p-3 bg-herb-50 rounded-full border border-herb-600/20 text-herb-700">
                                <CheckCircle2 className="h-6 w-6" />
                              </div>
                              <p className="font-semibold text-sm text-ink">Aucun écart de montant</p>
                              <p className="text-xs text-ink-faint max-w-md">
                                Les montants COD attendus (Shiparon) et déclarés par le transporteur sont parfaitement alignés.
                              </p>
                            </div>
                          ) : (
                            <div className="py-6">Aucun colis ne correspond aux critères sélectionnés.</div>
                          )}
                        </Td>
                      </Tr>
                    )}
                  </TBody>
                </Table>
              </TableShell>

              {/* Pagination Controls */}
              {parcelsQuery.data && parcelsQuery.data.totalPages > 1 && (
                <div className="flex items-center justify-between text-xs text-ink-soft px-1">
                  <div>
                    Affichage de{' '}
                    <span className="font-semibold text-ink">
                      {(page - 1) * 50 + 1} -{' '}
                      {Math.min(page * 50, parcelsQuery.data.total)}
                    </span>{' '}
                    sur <span className="font-semibold text-ink">{parcelsQuery.data.total}</span> colis
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page <= 1}
                      onClick={() => setPage((p) => Math.max(p - 1, 1))}
                    >
                      <ChevronLeft className="h-3.5 w-3.5 mr-0.5" />
                      Précédent
                    </Button>
                    <span className="px-2 font-mono">
                      {page} / {parcelsQuery.data.totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page >= parcelsQuery.data.totalPages}
                      onClick={() => setPage((p) => p + 1)}
                    >
                      Suivant
                      <ChevronRight className="h-3.5 w-3.5 ml-0.5" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Admin Payment Approval Modal */}
      {approvalModalOpen &&
        createPortal(
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-ink/50 backdrop-blur-xs overflow-y-auto"
            onClick={() => setApprovalModalOpen(false)}
          >
            <div
              className="bg-card border border-border rounded-2xl p-6 max-w-md w-full shadow-2xl space-y-4 animate-scale my-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between">
                <h3 className="text-base font-bold text-ink flex items-center gap-2">
                  <CheckCheck className="h-5 w-5 text-herb-700" />
                  Validation de l'encaissement (Admin)
                </h3>
                <button
                  type="button"
                  onClick={() => setApprovalModalOpen(false)}
                  className="text-ink-ghost hover:text-ink transition-colors p-1 rounded-lg hover:bg-cream"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <p className="text-xs text-ink-soft leading-relaxed">
                Vous confirmez que les fonds correspondants ont bien été reçus et vérifiés sur le compte bancaire de MaPara.
                {targetSettlementId && (
                  <span className="block mt-1 font-semibold text-herb-800">
                    Validation de l'ensemble du bordereau de règlement #{targetSettlementId}.
                  </span>
                )}
                {targetCodes.length > 0 && !targetSettlementId && (
                  <span className="block mt-1 font-semibold text-herb-800">
                    Validation de {targetCodes.length} colis sélectionné(s).
                  </span>
                )}
              </p>

              <div className="space-y-3 pt-2">
                <div>
                  <label className="block text-xs font-semibold text-ink mb-1">
                    Référence virement / bancaire (optionnel)
                  </label>
                  <input
                    type="text"
                    value={approvalBankRef}
                    onChange={(e) => setApprovalBankRef(e.target.value)}
                    placeholder="ex: VIR-STB-2026-10-02"
                    className="w-full text-xs bg-cream/40 border border-border rounded-lg px-3 py-2 text-ink focus:outline-none focus:ring-1 focus:ring-clay-600"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-ink mb-1">Notes comptables (optionnel)</label>
                  <textarea
                    value={approvalNotes}
                    onChange={(e) => setApprovalNotes(e.target.value)}
                    placeholder="Observations ou vérifications spécifiques..."
                    rows={2}
                    className="w-full text-xs bg-cream/40 border border-border rounded-lg px-3 py-2 text-ink focus:outline-none focus:ring-1 focus:ring-clay-600"
                  />
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-border">
                <Button variant="ghost" size="sm" onClick={() => setApprovalModalOpen(false)}>
                  Annuler
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  disabled={approveMutation.isPending}
                  onClick={() => {
                    const payload: {
                      codes?: string[];
                      settlementId?: number;
                      bankRef?: string;
                      notes?: string;
                    } = {};
                    if (targetCodes.length > 0) payload.codes = targetCodes;
                    if (targetSettlementId) payload.settlementId = targetSettlementId;
                    if (approvalBankRef.trim()) payload.bankRef = approvalBankRef.trim();
                    if (approvalNotes.trim()) payload.notes = approvalNotes.trim();
                    approveMutation.mutate(payload);
                  }}
                >
                  {approveMutation.isPending ? 'Validation…' : 'Confirmer l\'encaissement'}
                </Button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* Sync Date Range Modal */}
      {syncModalOpen &&
        createPortal(
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-ink/50 backdrop-blur-xs overflow-y-auto"
            onClick={() => setSyncModalOpen(false)}
          >
            <div
              className="bg-card border border-border rounded-2xl p-6 max-w-md w-full shadow-2xl space-y-4 animate-scale my-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between">
                <h3 className="text-base font-bold text-ink flex items-center gap-2">
                  <RefreshCw className="h-5 w-5 text-herb-700" />
                  Synchronisation Shiparon (MySQL)
                </h3>
                <button
                  type="button"
                  onClick={() => setSyncModalOpen(false)}
                  className="text-ink-ghost hover:text-ink transition-colors p-1 rounded-lg hover:bg-cream"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <p className="text-xs text-ink-soft leading-relaxed">
                Extraction en <strong>lecture seule stricte</strong> depuis la base <code>c1shipping.commande</code> via tunnel SSH.
              </p>

              <div className="space-y-3 pt-2">
                <div className="p-3 rounded-xl border border-clay-300 bg-clay-50/60 text-xs space-y-1.5">
                  <label className="flex items-center gap-2 font-bold text-clay-950 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={syncFullHistory}
                      onChange={(e) => setSyncFullHistory(e.target.checked)}
                      className="rounded text-clay-600 focus:ring-clay-500 h-4 w-4"
                    />
                    <span>Mode Backfill Complet (Depuis 1970)</span>
                  </label>
                  <p className="text-[11px] text-clay-800 leading-relaxed pl-6">
                    Pagine toutes les ~285k+ commandes jusqu'à épuisement, plus les 4 tables middleware (historique, runsheets, recettes, livreurs).
                  </p>
                </div>

                {!syncFullHistory && (
                  <div>
                    <label className="block text-xs font-semibold text-ink mb-1">Date de début (si partiel)</label>
                    <input
                      type="text"
                      value={syncStartDate}
                      onChange={(e) => setSyncStartDate(e.target.value)}
                      placeholder="2026-01-01 00:00:00"
                      className="w-full text-xs font-mono bg-cream/40 border border-border rounded-lg px-3 py-2 text-ink focus:outline-none focus:ring-1 focus:ring-clay-600"
                    />
                    <p className="text-[11px] text-ink-faint mt-1">
                      Par défaut : <code>2026-01-01 00:00:00</code> (~135 000 commandes).
                    </p>
                  </div>
                )}
              </div>

              {/* Active Sync Warning */}
              {latestSync?.status === 'running' && (
                <div className="p-3.5 rounded-xl border border-amber-300 bg-amber-50 text-xs space-y-2">
                  <div className="flex items-center gap-2 font-bold text-amber-950">
                    <RefreshCw className="h-4 w-4 text-amber-600 animate-spin" />
                    Une synchronisation est déjà active
                  </div>
                  <p className="text-amber-800 text-[11px] leading-relaxed">
                    {latestSync.progressStage || 'Progression en cours…'}
                  </p>
                  <div className="w-full bg-amber-200 rounded-full h-2 overflow-hidden">
                    <div
                      className="bg-amber-600 h-2 rounded-full transition-all duration-300"
                      style={{
                        width:
                          latestSync.totalRows && latestSync.totalRows > 0
                            ? `${Math.max(5, Math.min(100, (latestSync.rowCount / latestSync.totalRows) * 100))}%`
                            : '35%',
                      }}
                    />
                  </div>
                  <div className="text-[11px] font-mono text-amber-900 font-semibold text-right">
                    {latestSync.rowCount.toLocaleString('fr-FR')}{' '}
                    {latestSync.totalRows ? `/ ${latestSync.totalRows.toLocaleString('fr-FR')}` : ''} colis
                  </div>
                </div>
              )}

              {/* Sync Mutation Error */}
              {syncMutation.isError && (
                <div className="p-3 rounded-xl border border-rust-300 bg-rust-50 text-xs text-rust-900 flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-rust-600 shrink-0 mt-0.5" />
                  <div>
                    <strong>Échec du lancement :</strong>{' '}
                    {syncMutation.error instanceof Error ? syncMutation.error.message : 'Erreur réseau ou session expirée'}
                  </div>
                </div>
              )}

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-border">
                <Button variant="ghost" size="sm" onClick={() => setSyncModalOpen(false)}>
                  Fermer
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  disabled={syncMutation.isPending || latestSync?.status === 'running'}
                  onClick={() =>
                    syncMutation.mutate({
                      startDate: syncFullHistory ? '1970-01-01 00:00:00' : syncStartDate,
                      fullHistory: syncFullHistory,
                    })
                  }
                >
                  {syncMutation.isPending
                    ? 'Lancement…'
                    : latestSync?.status === 'running'
                    ? 'Synchronisation active…'
                    : 'Lancer la synchronisation'}
                </Button>
              </div>
            </div>
          </div>,
          document.body,
        )}
      {/* Parcel Detail Slide-Over Drawer (B3) */}
      {selectedParcelDetail &&
        createPortal(
          <div
            className="fixed inset-0 z-[100] flex justify-end bg-ink/40 backdrop-blur-xs transition-opacity"
            onClick={() => setSelectedParcelDetail(null)}
          >
            <div
              className="bg-card border-l border-border w-full max-w-lg h-full overflow-y-auto p-6 shadow-2xl flex flex-col justify-between animate-in slide-in-from-right duration-200"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="space-y-6">
                {/* Drawer Header */}
                <div className="flex items-start justify-between pb-4 border-b border-border">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="eyebrow text-ink-faint">Fiche Colis</span>
                      <span className="font-mono text-xs px-2 py-0.5 rounded bg-cream border border-border text-ink-soft">
                        {selectedParcelDetail.carrier || 'Transporteur inconnu'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <h2 className="text-lg font-bold font-mono text-ink tracking-tight">
                        {selectedParcelDetail.codeBarre}
                      </h2>
                      <button
                        type="button"
                        title="Copier le code barre"
                        onClick={() => {
                          navigator.clipboard.writeText(selectedParcelDetail.codeBarre);
                          setCopiedBarcode(selectedParcelDetail.codeBarre);
                          setTimeout(() => setCopiedBarcode(null), 1500);
                        }}
                        className="text-ink-ghost hover:text-ink p-1 rounded hover:bg-cream transition-colors"
                      >
                        {copiedBarcode === selectedParcelDetail.codeBarre ? (
                          <Check className="h-4 w-4 text-herb-700" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSelectedParcelDetail(null)}
                    className="text-ink-ghost hover:text-ink p-1.5 rounded-lg hover:bg-cream transition-colors"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                {/* Status Badges */}
                <div className="flex flex-wrap items-center gap-2">
                  {getStatusBadge(selectedParcelDetail.status, selectedParcelDetail.mismatchType, selectedParcelDetail.priceDiff)}
                  {getPaymentBadge(selectedParcelDetail.adminPaymentStatus)}
                </div>

                {/* Ghost Parcel Alert with Runsheet Proof */}
                {selectedParcelDetail.status === 'ghosted' && (
                  <div className="p-3.5 rounded-xl border border-rust-600/30 bg-rust-50/70 text-rust-900 text-xs space-y-2">
                    <div className="flex items-center gap-1.5 font-bold text-rust-950">
                      <ShieldAlert className="h-4 w-4 text-rust-600 shrink-0" />
                      <span>Colis Absent Transporteur non flashé</span>
                    </div>
                    <p className="text-rust-800 leading-relaxed">
                      Ce colis a été remis au transporteur sur Shiparon mais n'a reçu aucun scan de prise en charge sur son portail.
                    </p>
                    {selectedParcelDetail.idRunsheet ? (
                      <div className="pt-2 border-t border-rust-200/70 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 bg-white/80 p-2.5 rounded-lg border border-rust-300/50">
                        <div className="space-y-0.5">
                          <div className="text-[11px] font-semibold text-rust-950 flex items-center gap-1.5">
                            <Route className="h-3.5 w-3.5 text-rust-700" />
                            <span>Décharge officielle :</span>
                            <span className="font-mono font-bold text-rust-900">#{selectedParcelDetail.idRunsheet}</span>
                            {selectedParcelDetail.runsheetEtat === 1 ? (
                              <span className="text-[10px] font-medium bg-herb-100 text-herb-800 px-1.5 py-0.2 rounded border border-herb-200">
                                Tamponnée / Validée
                              </span>
                            ) : selectedParcelDetail.runsheetEtat !== null && selectedParcelDetail.runsheetEtat !== undefined ? (
                              <span className="text-[10px] font-medium bg-amber-100 text-amber-800 px-1.5 py-0.2 rounded">
                                État {selectedParcelDetail.runsheetEtat}
                              </span>
                            ) : null}
                          </div>
                          <div className="text-[10px] text-ink-soft">
                            {selectedParcelDetail.runsheetDate ? `Date: ${formatDateFr(selectedParcelDetail.runsheetDate)}` : ''}
                            {selectedParcelDetail.runsheetLivreur ? ` · Livreur: ${selectedParcelDetail.runsheetLivreur}` : ''}
                            {selectedParcelDetail.runsheetAgence ? ` · Agence: ${selectedParcelDetail.runsheetAgence}` : ''}
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <button
                            type="button"
                            onClick={() => {
                              navigator.clipboard.writeText(String(selectedParcelDetail.idRunsheet));
                              setCopiedBarcode(`modal-rs-${selectedParcelDetail.idRunsheet}`);
                              setTimeout(() => setCopiedBarcode(null), 1500);
                            }}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded bg-cream border border-border text-[11px] font-medium text-ink hover:bg-cream/80 transition-colors"
                            title="Copier le numéro de runsheet"
                          >
                            {copiedBarcode === `modal-rs-${selectedParcelDetail.idRunsheet}` ? (
                              <Check className="h-3 w-3 text-herb-700" />
                            ) : (
                              <Copy className="h-3 w-3 text-ink-ghost" />
                            )}
                            Copier N°
                          </button>
                          <Link
                            to={`/shipping/runsheets/${selectedParcelDetail.idRunsheet}`}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-clay-600 text-white text-[11px] font-medium hover:bg-clay-700 shadow-xs transition-colors"
                          >
                            Voir décharge →
                          </Link>
                        </div>
                      </div>
                    ) : (
                      <div className="text-[11px] text-rust-700 italic pt-1">
                        Aucun N° de feuille de route (runsheet) enregistré pour ce colis dans Shiparon.
                      </div>
                    )}
                  </div>
                )}

                {/* Unverified (En attente API) Explanation */}
                {selectedParcelDetail.status === 'unverified' && (
                  <div className="p-3.5 rounded-xl border border-amber-500/30 bg-amber-50/70 text-amber-950 text-xs space-y-1">
                    <div className="flex items-center gap-1.5 font-bold text-amber-950">
                      <Clock className="h-4 w-4 text-amber-700 shrink-0" />
                      <span>En attente de vérification API</span>
                    </div>
                    <p className="text-amber-900/90 leading-relaxed">
                      Ce colis a été créé dans Shiparon avec le transporteur <strong>{selectedParcelDetail.carrier || 'désigné'}</strong>, mais son statut n'a pas encore été vérifié via l'API de suivi du transporteur.
                    </p>
                  </div>
                )}

                {/* Unassigned Explanation */}
                {selectedParcelDetail.status === 'unassigned' && (
                  <div className="p-3.5 rounded-xl border border-border bg-cream/70 text-ink text-xs space-y-1">
                    <div className="flex items-center gap-1.5 font-bold text-ink">
                      <AlertTriangle className="h-4 w-4 text-ink-soft shrink-0" />
                      <span>Commande sans transporteur assigné</span>
                    </div>
                    <p className="text-ink-soft leading-relaxed">
                      Cette commande est enregistrée dans Shiparon sans prestataire désigné. Aucun suivi transporteur ne peut être effectué tant qu'elle n'est pas assignée à un transporteur.
                    </p>
                  </div>
                )}

                {/* Visual Milestone Stepper (Inspiration 8 & 9) */}
                <div className="p-3.5 bg-cream/40 border border-border/80 rounded-xl space-y-2">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-ink-faint">
                    Cycle de vie de la commande
                  </span>
                  <div className="grid grid-cols-4 gap-1.5 text-center">
                    {/* Step 1: Créé */}
                    <div className="space-y-1">
                      <div className="h-1.5 rounded-full bg-emerald-600" />
                      <span className="text-[10px] font-semibold text-ink block leading-tight">1. Créé</span>
                      <span className="text-[9px] text-ink-faint block">{formatDateFr(selectedParcelDetail.date)}</span>
                    </div>

                    {/* Step 2: Pris en charge */}
                    <div className="space-y-1">
                      <div className={`h-1.5 rounded-full ${
                        selectedParcelDetail.status !== 'ghosted' && selectedParcelDetail.status !== 'unassigned'
                          ? 'bg-emerald-600'
                          : 'bg-zinc-200'
                      }`} />
                      <span className="text-[10px] font-semibold text-ink block leading-tight">2. Pris en charge</span>
                      <span className="text-[9px] text-ink-faint block truncate">
                        {selectedParcelDetail.carrier || '—'}
                      </span>
                    </div>

                    {/* Step 3: Distribution */}
                    <div className="space-y-1">
                      <div className={`h-1.5 rounded-full ${
                        selectedParcelDetail.status === 'reconciled_paid' || selectedParcelDetail.status === 'reconciled_delivered' || selectedParcelDetail.status === 'reconciled_in_transit'
                          ? 'bg-emerald-600'
                          : selectedParcelDetail.status === 'reconciled_return'
                          ? 'bg-rose-500'
                          : 'bg-zinc-200'
                      }`} />
                      <span className="text-[10px] font-semibold text-ink block leading-tight">3. Tournée</span>
                      <span className="text-[9px] text-ink-faint block truncate">
                        {selectedParcelDetail.status === 'reconciled_return' ? 'Retour' : 'Livreur'}
                      </span>
                    </div>

                    {/* Step 4: Final */}
                    <div className="space-y-1">
                      <div className={`h-1.5 rounded-full ${
                        selectedParcelDetail.status === 'reconciled_paid' || selectedParcelDetail.adminPaymentStatus === 'approved'
                          ? 'bg-emerald-600'
                          : selectedParcelDetail.status === 'reconciled_delivered'
                          ? 'bg-amber-500'
                          : selectedParcelDetail.status === 'reconciled_return'
                          ? 'bg-rose-500'
                          : 'bg-zinc-200'
                      }`} />
                      <span className="text-[10px] font-semibold text-ink block leading-tight">4. Clôture</span>
                      <span className="text-[9px] text-ink-faint block truncate">
                        {selectedParcelDetail.status === 'reconciled_paid' ? 'Livré & Payé' : selectedParcelDetail.status === 'reconciled_return' ? 'Retourné' : 'En cours'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* 3-System Multi-Source Comparison Card (Shiparon vs Transporteur vs Banque) */}
                <div className="rounded-xl border border-border bg-card p-4 space-y-3">
                  <h3 className="text-xs font-bold text-ink uppercase tracking-wider flex items-center justify-between">
                    <span className="flex items-center gap-1.5">
                      <Coins className="h-3.5 w-3.5 text-herb-700" />
                      Rapprochement Multi-Sources 3 Niveaux
                    </span>
                    {selectedParcelDetail.mismatchType === 'underpaid' && (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-rust-100 text-rust-800 border border-rust-200">
                        Sous-payé (Déficit)
                      </span>
                    )}
                  </h3>

                  <div className="grid grid-cols-2 gap-3 text-xs">
                    {/* Level 1: Shiparon */}
                    <div className="p-2.5 rounded-lg bg-cream/50 border border-border/60">
                      <div className="text-[11px] font-semibold text-ink-soft flex items-center gap-1">
                        <span>1. Shiparon (Interne)</span>
                      </div>
                      <div className="text-sm font-bold font-mono text-ink mt-1">
                        {selectedParcelDetail.shiparonPrice !== null ? formatTnd(selectedParcelDetail.shiparonPrice) : '—'}
                      </div>
                      <div className="text-[10px] text-ink-faint mt-0.5">
                        État : {selectedParcelDetail.shiparonEtatLabel || 'Inconnu'} ({selectedParcelDetail.shiparonEtat})
                      </div>
                    </div>

                    {/* Level 2: Transporteur */}
                    <div className="p-2.5 rounded-lg bg-cream/50 border border-border/60">
                      <div className="text-[11px] font-semibold text-ink-soft flex items-center gap-1">
                        <span>2. Transporteur ({selectedParcelDetail.carrier || 'API'})</span>
                      </div>
                      <div className="text-sm font-bold font-mono text-ink mt-1">
                        {selectedParcelDetail.carrierPrice !== null ? formatTnd(selectedParcelDetail.carrierPrice) : '—'}
                      </div>
                      <div className="text-[10px] text-ink-faint mt-0.5">
                        Statut : {selectedParcelDetail.carrierStatus || (selectedParcelDetail.carrierPaye === 1 ? 'Payé' : 'Non vérifié')}
                      </div>
                    </div>
                  </div>

                  {/* Deficit or Surplus Alert */}
                  {selectedParcelDetail.priceDiff !== null && Math.abs(selectedParcelDetail.priceDiff) > 0.01 && (
                    <div
                      className={`p-2.5 rounded-lg border text-xs flex items-center justify-between ${
                        selectedParcelDetail.priceDiff < 0
                          ? 'bg-rust-50/80 border-rust-500/30 text-rust-950'
                          : 'bg-herb-50/80 border-herb-500/30 text-herb-950'
                      }`}
                    >
                      <div className="flex items-center gap-1.5 font-medium">
                        {selectedParcelDetail.priceDiff < 0 ? (
                          <>
                            <TrendingDown className="h-4 w-4 text-rust-600 shrink-0" />
                            <span>Déficit de versement transporteur :</span>
                          </>
                        ) : (
                          <>
                            <TrendingUp className="h-4 w-4 text-herb-600 shrink-0" />
                            <span>Surplus de versement transporteur :</span>
                          </>
                        )}
                      </div>
                      <span className={`font-mono font-bold ${selectedParcelDetail.priceDiff < 0 ? 'text-rust-700' : 'text-herb-700'}`}>
                        {selectedParcelDetail.priceDiff > 0 ? '+' : ''}{formatTnd(selectedParcelDetail.priceDiff)}
                      </span>
                    </div>
                  )}

                  {/* Level 3: MaPara Banking & Double Verification */}
                  <div className="pt-2 border-t border-border/70 space-y-1.5 text-xs text-ink-soft">
                    <div className="text-[11px] font-semibold text-ink uppercase tracking-wider">
                      3. Contrôle & Pointage Bancaire MaPara
                    </div>
                    <div className="flex justify-between">
                      <span className="text-ink-faint">Statut encaissement :</span>
                      <span className="font-medium text-ink">
                        {selectedParcelDetail.adminPaymentStatus === 'approved'
                          ? '✓ Encaissé & Validé MaPara'
                          : selectedParcelDetail.adminPaymentStatus === 'carrier_paid'
                          ? 'Déclaré payé transporteur (attente validation admin)'
                          : 'Non payé'}
                      </span>
                    </div>
                    {selectedParcelDetail.paymentApprovedBy && (
                      <div className="flex justify-between">
                        <span className="text-ink-faint">Validé par :</span>
                        <span className="font-mono text-ink">{selectedParcelDetail.paymentApprovedBy}</span>
                      </div>
                    )}
                    {selectedParcelDetail.paymentApprovedAt && (
                      <div className="flex justify-between">
                        <span className="text-ink-faint">Date validation :</span>
                        <span className="text-ink">{formatDateFr(selectedParcelDetail.paymentApprovedAt)}</span>
                      </div>
                    )}
                    {selectedParcelDetail.paymentBankRef && (
                      <div className="flex justify-between">
                        <span className="text-ink-faint">Réf bancaire :</span>
                        <span className="font-mono text-ink">{selectedParcelDetail.paymentBankRef}</span>
                      </div>
                    )}
                    {selectedParcelDetail.settlementBatch && (
                      <div className="flex justify-between">
                        <span className="text-ink-faint">Lot règlement :</span>
                        <span className="font-mono text-ink">{selectedParcelDetail.settlementBatch}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Tracking & Shiparon Details */}
                <div className="rounded-xl border border-border bg-card p-4 space-y-3">
                  <h3 className="text-xs font-bold text-ink uppercase tracking-wider flex items-center gap-1.5">
                    <Server className="h-3.5 w-3.5 text-ink-soft" />
                    Données Expédition & Suivi
                  </h3>
                  <div className="space-y-2 text-xs">
                    <div className="flex justify-between py-1 border-b border-border/50">
                      <span className="text-ink-faint">Date d'expédition :</span>
                      <span className="font-medium text-ink">{formatDateFr(selectedParcelDetail.date)}</span>
                    </div>
                    <div className="flex justify-between py-1 border-b border-border/50">
                      <span className="text-ink-faint">État Shiparon :</span>
                      <span className="font-medium text-ink">
                        {selectedParcelDetail.shiparonEtatLabel || '—'}
                        {selectedParcelDetail.shiparonEtat !== null ? ` (code ${selectedParcelDetail.shiparonEtat})` : ''}
                      </span>
                    </div>
                    <div className="flex justify-between py-1 border-b border-border/50">
                      <span className="text-ink-faint">Statut transporteur :</span>
                      <span className="font-medium text-ink">{selectedParcelDetail.carrierStatus || '—'}</span>
                    </div>
                    {selectedParcelDetail.idRunsheet && (
                      <div className="flex justify-between items-center py-1 border-b border-border/50">
                        <span className="text-ink-faint">Feuille de route (Runsheet) :</span>
                        <div className="flex items-center gap-1.5">
                          <Link
                            to={`/shipping/runsheets/${selectedParcelDetail.idRunsheet}`}
                            className="font-mono font-bold text-clay-700 hover:text-clay-900 hover:underline flex items-center gap-1"
                          >
                            <Route className="h-3 w-3" />
                            #{selectedParcelDetail.idRunsheet}
                          </Link>
                          {selectedParcelDetail.runsheetEtat === 1 && (
                            <span className="text-[10px] bg-herb-100 text-herb-800 px-1 rounded border border-herb-200">
                              Tamponnée
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    {(timelineQuery.data?.runsheet?.[0]?.livreur ||
                      (timelineQuery.data?.historique ? [...timelineQuery.data.historique].reverse().find((h) => h.livreur)?.livreur : null)) && (
                      <div className="flex justify-between py-1 border-b border-border/50">
                        <span className="text-ink-faint">Livreur / Manifeste :</span>
                        <span className="font-medium text-clay-700">
                          {timelineQuery.data?.runsheet?.[0]?.livreur ||
                            [...(timelineQuery.data?.historique ?? [])].reverse().find((h) => h.livreur)?.livreur}
                        </span>
                      </div>
                    )}
                    {selectedParcelDetail.motif && (
                      <div className="flex justify-between py-1">
                        <span className="text-ink-faint">Motif :</span>
                        <span className="font-medium text-ink">{selectedParcelDetail.motif}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Client & Recipient Information */}
                <div className="rounded-xl border border-border bg-card p-4 space-y-3">
                  <h3 className="text-xs font-bold text-ink uppercase tracking-wider flex items-center gap-1.5">
                    <Phone className="h-3.5 w-3.5 text-ink-soft" />
                    Destinataire & Coordonnées
                  </h3>
                  <div className="space-y-2 text-xs">
                    <div className="flex justify-between py-1 border-b border-border/50">
                      <span className="text-ink-faint">Nom du client :</span>
                      <span className="font-bold text-ink">{selectedParcelDetail.clientName || '—'}</span>
                    </div>
                    <div className="flex justify-between items-center py-1 border-b border-border/50">
                      <span className="text-ink-faint">Téléphone :</span>
                      {selectedParcelDetail.clientPhone ? (
                        <a
                          href={`tel:${selectedParcelDetail.clientPhone.replace(/\D/g, '')}`}
                          className="inline-flex items-center gap-1.5 font-mono font-semibold text-clay-700 hover:text-clay-800 bg-clay-50 px-2 py-0.5 rounded border border-clay-200/50"
                        >
                          <Phone className="h-3 w-3" />
                          {formatTunisianPhone(selectedParcelDetail.clientPhone)}
                        </a>
                      ) : (
                        <span className="text-ink-ghost">—</span>
                      )}
                    </div>
                    <div className="flex justify-between py-1">
                      <span className="text-ink-faint">Destination :</span>
                      <span className="text-ink font-medium text-right max-w-[240px]">
                        {selectedParcelDetail.location || '—'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Historique des événements Shiparon (Parité Middleware) */}
                <div className="rounded-xl border border-border bg-card p-4 space-y-3">
                  <h3 className="text-xs font-bold text-ink uppercase tracking-wider flex items-center justify-between">
                    <span className="flex items-center gap-1.5">
                      <History className="h-3.5 w-3.5 text-ink-soft" />
                      Historique des événements (Shiparon)
                    </span>
                    {timelineQuery.data?.historique && timelineQuery.data.historique.length > 0 && (
                      <span className="text-[10px] font-mono text-ink-ghost">
                        {timelineQuery.data.historique.length} événement(s)
                      </span>
                    )}
                  </h3>

                  {timelineQuery.isLoading ? (
                    <div className="py-4 flex justify-center">
                      <Loading />
                    </div>
                  ) : timelineQuery.data?.historique && timelineQuery.data.historique.length > 0 ? (
                    <div className="divide-y divide-border/50 text-xs">
                      {timelineQuery.data.historique.map((evt) => (
                        <div key={evt.id} className="py-2 flex items-start justify-between gap-3">
                          <div className="space-y-0.5">
                            <div className="font-semibold text-ink flex items-center gap-1.5">
                              <span>{evt.etatLabel}</span>
                              <span className="text-[10px] text-ink-faint font-mono">(code {evt.etat})</span>
                            </div>
                            <div className="text-[11px] text-ink-soft">
                              {evt.livreur ? (
                                <span className="font-medium text-clay-700">Livreur : {evt.livreur}</span>
                              ) : evt.magasin ? (
                                <span>Opérateur : {evt.magasin}</span>
                              ) : (
                                <span className="text-ink-ghost">Système</span>
                              )}
                              {evt.motif && <span className="ml-2 italic text-ink-ghost">— {evt.motif}</span>}
                            </div>
                          </div>
                          <div className="text-[11px] font-mono text-ink-soft whitespace-nowrap text-right">
                            {formatDateTimeFr(evt.dt)}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="py-3 text-center text-xs text-ink-ghost italic">
                      Aucun scan d'historique enregistré pour ce colis (import direct ou commande initiale).
                    </div>
                  )}

                  {timelineQuery.data?.runsheet && timelineQuery.data.runsheet.length > 0 && (
                    <div className="pt-2 border-t border-border/60 space-y-1.5">
                      <div className="text-[11px] font-semibold text-ink-soft">Manifeste Livreur (Runsheet)</div>
                      {timelineQuery.data.runsheet.map((rs) => (
                        <div key={rs.id} className="text-xs p-2 rounded-lg bg-cream/40 border border-border/50 flex justify-between items-center">
                          <div>
                            <div className="flex items-center gap-1.5">
                              {rs.idRunsheet ? (
                                <Link
                                  to={`/shipping/runsheets/${rs.idRunsheet}`}
                                  className="font-mono font-bold text-clay-700 hover:text-clay-900 hover:underline flex items-center gap-1"
                                >
                                  <Route className="h-3 w-3" />
                                  Tournée #{rs.idRunsheet}
                                </Link>
                              ) : null}
                              <span className="font-semibold text-ink">{rs.livreur || 'Livreur non assigné'}</span>
                            </div>
                            {rs.matricule && <div className="text-[10px] text-ink-faint font-mono">Véhicule : {rs.matricule}</div>}
                          </div>
                          <div className="text-right text-[11px] font-mono text-ink-soft">
                            {rs.dt ? formatDateTimeFr(rs.dt) : '—'}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Footer Actions */}
              <div className="pt-6 border-t border-border flex items-center justify-between gap-3">
                <Button variant="outline" size="sm" onClick={() => setSelectedParcelDetail(null)}>
                  Fermer
                </Button>
                {selectedParcelDetail.adminPaymentStatus !== 'approved' && (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => {
                      setTargetCodes([selectedParcelDetail.codeBarre]);
                      setTargetSettlementId(null);
                      setApprovalModalOpen(true);
                    }}
                  >
                    <CheckCheck className="h-4 w-4 mr-1.5" />
                    Valider l'encaissement
                  </Button>
                )}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
